<template>
  <div
    v-if="visible"
    class="cbs-agent"
  >
    <!-- Кнопка включения прижата к правому краю окна и видна на ЛЮБОМ экране: панель нужна
         там, где человек работает, а не там, куда он должен за ней сходить. Когда панель
         открыта, кнопка едет вместе с её краем — иначе она осталась бы под панелью. -->
    <button
      class="cbs-agent-toggle"
      :class="{ 'cbs-agent-toggle--open': mode !== 'off' }"
      :style="mode === 'off' ? null : { right: width + 'px' }"
      :title="mode === 'off' ? 'Спросить агента' : 'Скрыть панель агента'"
      @click="toggle"
    >
      <!-- PROP-399: иконка кнопки чата — та же CpuIcon, что и в заголовке панели (аватар
           «Агент»), чтобы кнопка открытия и шапка читались как одно. В открытом состоянии
           остаётся шеврон-сворачивание. -->
      <feather-icon
        :icon="mode === 'off' ? 'CpuIcon' : 'ChevronRightIcon'"
        size="16"
      />
    </button>

    <aside
      v-if="mode !== 'off'"
      class="cbs-agent-panel"
      :class="'cbs-agent-panel--' + mode"
      :style="{ width: width + 'px' }"
    >
      <div
        class="cbs-agent-resizer"
        :class="{ 'cbs-agent-resizer--active': resizing }"
        title="Потяните, чтобы изменить ширину"
        @mousedown.prevent="startResize"
        @dblclick="setWidth(DEFAULT_WIDTH)"
      />

      <div class="cbs-agent-header">
        <b-avatar
          size="26"
          :style="{ backgroundColor: '#00cfe8' }"
        >
          <feather-icon
            icon="CpuIcon"
            size="14"
          />
        </b-avatar>
        <div class="cbs-agent-title">
          <span class="cbs-agent-name">Агент</span>
          <!-- BRG-442: состояние ТОГО агента, которому человек сейчас пишет. Раньше здесь
               всегда стояло состояние чат-сокета, и при выбранном локальном Клоде шапка
               показывала зелёное «на связи», пока мост не был даже установлен. -->
          <small
            class="cbs-agent-status"
            :class="{
              'text-success': agentTone === 'ok',
              'text-warning': agentTone === 'wait',
              'text-danger': agentTone === 'bad',
            }"
          >{{ agentStatusLabel }}</small>
        </div>
        <!-- PROP-468: текущий режим работы агента виден БЕЗ открытия настроек. Иначе отказ
             «структуру менять нельзя» читается как поломка: человек не видит, в каком режиме
             агент работает. Сам переключатель живёт в настройках, рядом с прочими списками. -->
        <small
          v-if="agentMode"
          class="cbs-agent-modebadge"
          :class="agentMode === 'builder' ? 'cbs-agent-modebadge--builder' : ''"
          :title="agentModeTitle"
        >{{ agentModeLabel }}</small>
        <div class="cbs-agent-actions">
          <!-- PROP-433: настройки убраны под шестерёнку. Три выпадающих списка занимали верх
               панели постоянно, а трогают их изредка: место в узкой колонке нужнее переписке. -->
          <b-button
            size="sm"
            :variant="settingsOpen ? 'primary' : 'outline-secondary'"
            class="cbs-agent-mode"
            :title="settingsOpen ? 'Скрыть настройки' : 'Настройки'"
            @click="toggleSettings"
          >
            <feather-icon
              icon="SettingsIcon"
              size="13"
            />
          </b-button>
          <b-button
            size="sm"
            :variant="mode === 'overlay' ? 'primary' : 'outline-secondary'"
            class="cbs-agent-mode"
            title="Поверх страницы"
            @click="setMode('overlay')"
          >
            <feather-icon
              icon="LayersIcon"
              size="13"
            />
          </b-button>
          <b-button
            size="sm"
            :variant="mode === 'docked' ? 'primary' : 'outline-secondary'"
            class="cbs-agent-mode"
            title="Рядом со страницей"
            @click="setMode('docked')"
          >
            <feather-icon
              icon="ColumnsIcon"
              size="13"
            />
          </b-button>
          <b-button
            size="sm"
            variant="flat-secondary"
            class="cbs-agent-mode"
            title="Закрыть"
            @click="setMode('off')"
          >
            <feather-icon
              icon="XIcon"
              size="13"
            />
          </b-button>
        </div>
      </div>

      <!-- PROP-396: выбор режима «Чат / Отладка» — как на странице чат-бота.
           PROP-402: рядом — режим подачи ответа «Чат / Презентация». В презентации агент
           оформляет ответ самостоятельной HTML-страницей, и она открывается в основной области.
           Лента по умолчанию — «Отладка» (указание владельца 2026-09-10: ход работы виден сразу),
           подача ответа — «Чат» (указание владельца 2026-09-23; прежде «Презентация»).
           Настройки закрыты (PROP-433) — какой режим стоит, видно только открыв их. -->
      <div
        v-if="settingsOpen"
        class="cbs-agent-toolbar"
      >
        <b-form-select
          v-model="chatMode"
          :options="chatModeOptions"
          size="sm"
          class="cbs-agent-mode-select"
        />
        <b-form-select
          v-model="presentMode"
          :options="presentModeOptions"
          size="sm"
          class="cbs-agent-mode-select"
        />
        <!-- PROP-656: кому разрешено отвечать — тот же выбор, что на странице чата. Для локального
             Клода не показывается: он отвечает мимо бэкенда, и режим там ничего не меняет. -->
        <b-form-select
          v-if="agentKind !== 'local'"
          v-model="aiMode"
          :options="aiModeOptions"
          size="sm"
          class="cbs-agent-mode-select"
          title="Кому разрешено отвечать"
        />
        <!-- PROP-419: какой агент отвечает.
             PROP-425: «Локальный Клод» — это Claude Code на машине человека, через мост
             `cubus-claude-bridge`. Запрос идёт с этой страницы прямо на `127.0.0.1`, минуя
             бэкенд Cubus, поэтому у режима свои настройки и своё состояние. -->
        <b-form-select
          v-model="agentKind"
          :options="agentKindOptionsShown"
          size="sm"
          class="cbs-agent-kind-select"
          title="Какой агент отвечает"
          @change="onAgentKindChange"
        />
        <!-- PROP-468: режим работы агента. Показывается только там, где он ДЕЙСТВУЕТ:
             `agentMode === null` означает домен, раскатанный до PROP-464. -->
        <b-form-select
          v-if="agentMode"
          v-model="agentMode"
          :options="agentModeOptions"
          size="sm"
          class="cbs-agent-kind-select"
          :title="agentModeTitle"
          @change="onAgentModeChange"
        />
      </div>

      <!-- BRG-415: подключение к мосту. Токена здесь нет и быть не может: запрос идёт через
           окно, которое отдаёт сам мост, и токен живёт там (BRG-417). Человек нажимает
           «Подключить», подтверждает папку и срок в системном окне на своей машине — и видит,
           к какой папке и до какого времени выдан доступ. -->
      <div
        v-if="settingsOpen && agentKind === 'local'"
        class="cbs-agent-bridge"
      >
        <div class="cbs-agent-bridge-title">
          Мост к вашему Claude Code
        </div>
        <b-form-input
          v-if="!bridgeConnected"
          v-model="bridgeUrl"
          size="sm"
          placeholder="http://127.0.0.1:3777"
          class="mb-1"
          @change="onBridgeUrlChange"
        />
        <div
          v-if="bridgeConnected"
          class="cbs-agent-bridge-note"
        >
          Подключено: папка «{{ bridgeFolder }}», доступ до {{ bridgeUntil }}.
          <template v-if="bridgeToolsAllowed">
            Инструменты Cubus подключены.
          </template>
          <template v-else>
            <strong>Инструменты Cubus не разрешены этим доступом</strong> — агент не увидит
            данных домена. Нажмите «Отключить», затем «Подключить»: в системном окне появится
            адрес инструментов.
          </template>
          Окно моста должно оставаться открытым — через него идут запросы.
        </div>
        <div
          v-else
          class="cbs-agent-bridge-note"
        >
          Откроется окно моста, а на вашем компьютере — системный запрос: какую папку открыть
          и подтверждаете ли вы доступ. Токен сюда не попадает вовсе.
        </div>
        <div class="cbs-agent-bridge-actions">
          <b-button
            v-if="!bridgeConnected"
            size="sm"
            variant="primary"
            :disabled="bridgeBusy"
            @click="connectToBridge"
          >
            {{ bridgeBusy ? 'Подключение…' : 'Подключить' }}
          </b-button>
          <b-button
            v-else
            size="sm"
            variant="outline-secondary"
            @click="disconnectFromBridge"
          >
            Отключить
          </b-button>
          <span class="cbs-agent-bridge-status">{{ bridgeStatusLabel }}</span>
        </div>
        <div
          v-if="bridgeError"
          class="cbs-agent-bridge-error"
        >
          {{ bridgeError }}
        </div>

        <!-- BRG-441: как мост вообще завести. Раньше человеку без моста взяться было неоткуда:
             единственным признаком было «мост не отвечает», и что делать дальше — ни слова.
             Раскрыт по умолчанию ровно тогда, когда мост не отвечает: в этот момент вопрос и
             возникает. Когда всё работает — свёрнут, чтобы не мозолить глаза. -->
        <div
          v-if="!bridgeConnected"
          class="cbs-agent-install"
        >
          <button
            type="button"
            class="cbs-agent-install-toggle"
            @click="installOpen = !installOpen"
          >
            {{ installOpen ? '▾' : '▸' }} Моста ещё нет? Как установить
          </button>
          <div
            v-if="installOpen"
            class="cbs-agent-install-body"
          >
            <!-- BRG-441 (Windows): от ОС зависят и команды, и число шагов. macOS ставится одной
                 строкой из раздачи; на Windows поставки-службы пока нет — сборка из исходников.
                 Умолчание выбирается по системе пользователя, переключатель это перекрывает. -->
            <div class="cbs-agent-install-os">
              <button
                type="button"
                :class="['cbs-agent-install-os-btn', { active: installOs === 'mac' }]"
                @click="installOs = 'mac'"
              >
                macOS
              </button>
              <button
                type="button"
                :class="['cbs-agent-install-os-btn', { active: installOs === 'win' }]"
                @click="installOs = 'win'"
              >
                Windows
              </button>
            </div>

            <div class="cbs-agent-install-step">
              <b>1.</b> Нужен установленный Claude Code и выполненный в нём вход
              (<code>claude auth login</code>) — мост работает на вашей подписке.
            </div>

            <!-- macOS: одна строка из раздачи, дальше мост поднимается сам. -->
            <template v-if="installOs === 'mac'">
              <div class="cbs-agent-install-step">
                <b>2.</b> Выполните в Терминале:
                <div class="cbs-agent-install-cmd">
                  <code>{{ installCommand }}</code>
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="cbs-agent-install-copy"
                    :title="installCopied ? 'Скопировано' : 'Скопировать команду'"
                    @click="copyInstallCommand"
                  >
                    {{ installCopied ? 'Скопировано' : 'Копировать' }}
                  </b-button>
                </div>
              </div>
              <div class="cbs-agent-install-step">
                <b>3.</b> Мост поднимется сам и будет запускаться при входе в систему.
                Проверить: <code>claude-bridge status</code>. Затем нажмите «Подключить».
              </div>
            </template>

            <template v-else>
              <!-- Windows: Node + сборка из исходников. Node встаёт первой командой, но PATH
                   обновляется только в НОВОМ окне PowerShell — поэтому остальное отдельным
                   заходом; об этом прямо сказано в шаге 3. -->
              <div class="cbs-agent-install-step">
                <b>2.</b> Установите Node.js 20+
                (<code>winget install OpenJS.NodeJS.LTS</code>), затем закройте и снова
                откройте PowerShell.
              </div>
              <div class="cbs-agent-install-step">
                <b>3.</b> В новом окне PowerShell выполните по порядку:
                <div class="cbs-agent-install-cmd">
                  <code>{{ installCommand }}</code>
                  <b-button
                    size="sm"
                    variant="outline-secondary"
                    class="cbs-agent-install-copy"
                    :title="installCopied ? 'Скопировано' : 'Скопировать команды'"
                    @click="copyInstallCommand"
                  >
                    {{ installCopied ? 'Скопировано' : 'Копировать' }}
                  </b-button>
                </div>
              </div>
              <div class="cbs-agent-install-step">
                <b>4.</b> Мост поднимется на <code>127.0.0.1:3777</code> — дальше нажмите
                «Подключить». Следующие запуски — просто <code>npm start</code>; для
                автозапуска положите <code>start.bat</code> в «Автозагрузку»
                (<code>shell:startup</code>).
              </div>
            </template>

            <div
              v-if="installCopyFailed"
              class="cbs-agent-install-note text-danger"
            >
              Скопировать не удалось — выделите команду выше и скопируйте вручную.
            </div>
            <div class="cbs-agent-install-note">
              Мост слушает только ваш компьютер (<code>127.0.0.1</code>) и наружу не
              выставляется.
            </div>
          </div>
        </div>
      </div>

      <div
        ref="log"
        class="cbs-agent-log"
        @click="onLogClick"
      >
        <div
          v-if="!visibleMessages.length"
          class="cbs-agent-empty"
        >
          Спросите агента о том, что видите на экране. Он работает в текущем домене
          и под вашими правами.
        </div>
        <div
          v-for="(m, i) in visibleMessages"
          :key="i"
          class="cbs-agent-row"
          :class="{
            'cbs-agent-row--user': m.sender === 'user',
            'cbs-agent-row--service': m.service,
          }"
        >
          <!-- Веха хода работы: не реплика, а показания приборов. Тот же приглушённый вид,
               что и на странице чата (PROP-366), чтобы одно и то же читалось одинаково. -->
          <div
            v-if="m.service"
            class="cbs-agent-service"
          >
            <span v-html="render(m.text)" />
            <span class="cbs-agent-service-time">{{ time(m.at) }}</span>
          </div>
          <div
            v-else
            class="cbs-agent-bubble"
            :class="m.sender === 'user' ? 'cbs-agent-bubble--user' : 'cbs-agent-bubble--bot'"
          >
            <div class="cbs-agent-meta">
              <span>{{ m.author }}</span>
              <span>{{ time(m.at) }}</span>
            </div>
            <div v-html="render(m.text)" />
            <!-- RFILE-590: файлы результата — кнопкой под ответом, а не ссылкой в тексте (ТЗ §8 п. 2).
                 Имя и причина выводятся ИНТЕРПОЛЯЦИЕЙ: их пишет агент, и `v-html` здесь означал бы,
                 что имя файла может стать разметкой в окне Cubus. -->
            <div
              v-if="m.files && m.files.length"
              class="cbs-agent-files"
            >
              <button
                v-for="f in m.files"
                :key="f.id"
                type="button"
                class="cbs-agent-file"
                :disabled="fileBusy(f)"
                :title="`Скачать «${f.name}»`"
                @click="downloadResultFile(f)"
              >
                <span class="cbs-agent-file-icon">⬇</span>
                <span class="cbs-agent-file-name">{{ f.name }}</span>
                <span class="cbs-agent-file-size">{{ fileBusy(f) ? 'скачиваю…' : fileSize(f.size) }}</span>
              </button>
              <div
                v-for="f in m.files.filter(x => fileError(x))"
                :key="`e${f.id}`"
                class="cbs-agent-file-error"
              >
                {{ f.name }}: {{ fileError(f) }}
              </div>
            </div>
            <!-- RFILE-613: место «принимается» — файл объявлен, но ещё проверяется (ТЗ «Фоновая приёмка»
                 §8 п. 1). Кнопки нет: скачивать нечего. Имя — интерполяцией, как у кнопок. -->
            <div
              v-if="m.pendingFiles && m.pendingFiles.length"
              class="cbs-agent-files-pending"
            >
              <div
                v-for="(f, k) in m.pendingFiles"
                :key="`p${k}`"
              >
                ⏳ {{ f.name }} — принимается…
              </div>
            </div>
            <div
              v-if="m.filesFailed && m.filesFailed.length"
              class="cbs-agent-files-failed"
            >
              <div
                v-for="(f, k) in m.filesFailed"
                :key="k"
              >
                ⚠️ {{ f.name }}{{ f.reason ? ` — ${f.reason}` : '' }}
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="waiting"
          class="cbs-agent-row"
        >
          <div class="cbs-agent-bubble cbs-agent-bubble--bot cbs-agent-typing">
            {{ waitingWho }} думает
            <span class="cbs-agent-dot" />
            <span class="cbs-agent-dot" />
            <span class="cbs-agent-dot" />
            <!-- PROP-408: сколько уже ждём. Ответ агента идёт минутами, и без счётчика «думает»
                 неотличимо от зависшего: человеку нужно понимать, десять секунд прошло или пять
                 минут. Считаем от ОТПРАВКИ, служебные строки счётчик не сбрасывают. -->
            <span class="cbs-agent-elapsed">{{ waitingLabel }}</span>
          </div>
        </div>
      </div>

      <form
        class="cbs-agent-input"
        @submit.prevent="onSubmit"
      >
        <!-- Команды доступны и здесь: панель — рабочее место, а не только окно разговора.
             Реестр тот же, что на странице чата (PROP-384). -->
        <div
          v-if="commandHints.length"
          class="cbs-agent-commands"
        >
          <div
            v-for="(c, i) in commandHints"
            :key="c.cmd"
            class="cbs-agent-command"
            :class="{ 'cbs-agent-command--active': i === commandIndex }"
            @mousedown.prevent="pickCommand(c)"
            @mouseenter="commandIndex = i"
          >
            <span class="cbs-agent-command-name">{{ c.cmd }}</span>
            <span class="cbs-agent-command-hint">{{ c.hint }}</span>
          </div>
        </div>
        <!-- FILE-516: прикреплённые к заданию файлы. Уже загружены на сервер (отказ по пределам
             пришёл бы при выборе); крестик убирает файл из задания, а не из хранилища. -->
        <div
          v-if="attachments.length"
          class="cbs-agent-attachments"
        >
          <span
            v-for="a in attachments"
            :key="a.id"
            class="cbs-agent-attachment"
            :title="`${a.name} · ${fileSize(a.size)}`"
          >
            📎 {{ a.name }} <small class="text-muted">{{ fileSize(a.size) }}</small>
            <button
              type="button"
              class="cbs-agent-attachment-remove"
              title="Убрать из задания"
              @click="removeAttachment(a.id)"
            >×</button>
          </span>
        </div>
        <input
          ref="attachInput"
          type="file"
          multiple
          class="d-none"
          :accept="attachAccept"
          @change="onFilesChosen"
        >
        <!-- PROP-418: поле резиновое — растёт под текст до восьми строк, дальше прокручивается.
             Однострочный ввод прятал длинный запрос: видно было только хвост, и перечитать
             написанное перед отправкой было нечем. Enter отправляет (как и раньше),
             Shift+Enter — перенос строки. -->
        <b-form-textarea
          ref="panelInput"
          v-model="text"
          size="sm"
          rows="1"
          max-rows="8"
          no-resize
          class="cbs-agent-textarea"
          :placeholder="inputLocked
            ? 'Локальный Клод отвечает — дождитесь ответа…'
            : 'Спросить агента или «/» для команд…'"
          autocomplete="off"
          @keydown.enter.exact="onEnter"
          @keydown.up="onArrowUp"
          @keydown.down="onArrowDown"
          @keydown.tab="onTab"
          @keydown.esc="commandsDismissed = true"
        />
        <!-- FILE-516: кнопка есть, только если сервер сказал, что прикрепление доступно.
             BFILE-558: у локального Клода условий ТРИ — признак в whoami, возможность files у
             моста и разрешение в гранте; не сошлось хотя бы одно — скрепки нет. -->
        <b-button
          v-if="canAttach"
          size="sm"
          variant="outline-secondary"
          :disabled="attachUploading || inputLocked"
          :title="attachUploading ? 'Файл загружается…' : 'Прикрепить файл к заданию'"
          @click="$refs.attachInput && $refs.attachInput.click()"
        >
          <feather-icon
            :icon="attachUploading ? 'LoaderIcon' : 'PaperclipIcon'"
            size="14"
          />
        </b-button>
        <!-- BFILE-558 (ТЗ моста §13 п. 2): мост есть, а файлов не умеет. Молча спрятать скрепку
             значило бы оставить человека гадать, почему у соседа она есть, а у него нет. -->
        <span
          v-else-if="bridgeUpdateHint"
          class="cbs-agent-hint"
          :title="bridgeUpdateHint"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="14"
          />
        </span>
        <!-- RFILE-630 (ТЗ «Мост: файлы результата» §19 п. 3): платформа отдаёт файлы результата, а мост — нет:
             почему и что сделать. Молчание оставило бы человека гадать, почему раннер файлы присылает, а мост нет. -->
        <span
          v-if="agentKind === 'local' && bridgeResultHint"
          class="cbs-agent-hint cbs-agent-result-hint"
          :title="bridgeResultHint"
        >
          <feather-icon
            icon="DownloadCloudIcon"
            size="14"
          />
        </span>
        <!-- PROP-393: голосовой ввод — та же реализация, что на странице чата. Кнопки нет
             там, где Web Speech API не поддержан: показывать неработающую кнопку хуже, чем
             не показывать её вовсе. -->
        <b-button
          v-if="isSpeechSupported"
          size="sm"
          :variant="isRecording ? 'danger' : 'outline-secondary'"
          :title="isRecording ? 'Остановить запись' : 'Голосовой ввод'"
          @click="toggleVoiceInput"
        >
          <feather-icon
            icon="MicIcon"
            size="14"
          />
        </b-button>
        <!-- BRG-435: пока идёт ответ, на месте «Отправить» стоит «Прервать». Так у ожидания
             есть выход: без него оставалось только ждать десятиминутный таймаут моста. -->
        <b-button
          v-if="inputLocked"
          size="sm"
          variant="outline-danger"
          type="button"
          :disabled="bridgeCancelling"
          title="Прервать запрос"
          @click="cancelAsk"
        >
          <feather-icon
            icon="SquareIcon"
            size="14"
          />
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="primary"
          type="submit"
          :disabled="!text.trim() || attachUploading || waiting"
          title="Отправить"
        >
          <feather-icon
            icon="SendIcon"
            size="14"
          />
        </b-button>
      </form>
    </aside>
    <!-- PROP-754 (оферта п. 8.6): подтверждение ответственности перед подключением моста. Отметка по умолчанию
         снята, «Подключить» неактивна; по «Подключить» окно моста открывается в том же нажатии. -->
    <b-modal
      v-model="bridgeConsentOpen"
      :title="bridgeConsentText.title"
      hide-footer
      size="lg"
      data-own-ai-consent
    >
      <div
        class="d-flex justify-content-end mb-2"
        data-own-ai-langs
      >
        <b-button
          v-for="l in consentLangs"
          :key="l.value"
          size="sm"
          class="ml-1"
          :variant="bridgeConsentLang === l.value ? 'primary' : 'outline-secondary'"
          :data-own-ai-lang="l.value"
          @click="bridgeConsentLangSel = l.value"
        >
          {{ l.text }}
        </b-button>
      </div>
      <p class="mb-2">
        {{ bridgeConsentText.intro }}
      </p>
      <ul class="pl-3 mb-2">
        <li
          v-for="pt in bridgeConsentText.points"
          :key="pt[0]"
          class="mb-1"
        >
          <b>{{ pt[0] }}</b> {{ pt[1] }}
        </li>
      </ul>
      <label class="d-flex align-items-start border rounded p-2 mb-1">
        <input
          v-model="bridgeConsentChecked"
          type="checkbox"
          class="mt-1 mr-2"
          data-own-ai-check
        >
        <span>{{ bridgeConsentText.check }}</span>
      </label>
      <small
        v-if="bridgeConsentText.draftNote"
        class="text-muted d-block mb-2"
      >{{ bridgeConsentText.draftNote }}</small>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          @click="bridgeConsentOpen = false"
        >
          {{ bridgeConsentText.cancel }}
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="!bridgeConsentChecked"
          data-own-ai-connect
          @click="onBridgeConsentOk"
        >
          {{ bridgeConsentText.connect }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BAvatar, BButton, BFormTextarea, BFormSelect, BFormInput, BModal,
} from 'bootstrap-vue'
import {
  bridgeConsentState, bridgeConsentText, confirmBridgeConsent, CONSENT_LANGS,
} from '@/cubus/services/ownAiConsent'
import { addSource, renderSourcesLine, sourceOfToolCall } from '@/cubus/services/agentSources'
import {
  renderChatMarkdown, handleInternalClick, internalLinksOf, saysDoNotOpen, wantsOpenObject,
} from '@/cubus/services/chatMarkdown'
import { extractPresentation, setPresentation } from '@/cubus/services/chatPresentation'
import {
  askBridgeStream, cancelBridge, newRequestId,
  bridgeHealth, connectBridge, disconnectBridge, getBridgeUrl, setBridgeUrl,
  reportLocalRun,
  isBridgeConnected, startLocalRun, onBridgeChange, syncDomainSnapshot,
  bridgeCanFiles, bridgeBuild, bridgeFilesAllowed, putAttachmentsToBridge, bridgeResult,
  bridgeCanResultFiles, bridgeKnowsResultFiles, bridgeResultFilesAllowed, fetchResultFileFromBridge, uploadResultFileToCubus,
} from '@/cubus/services/claudeBridge'
import { openChatSocket } from '@/cubus/services/chatSocket'
import {
  parseCommand, matchCommands, HELP_TEXT, atFirstLine, atLastLine,
} from '@/cubus/services/chatCommands'
import {
  MODE_OPTIONS as AGENT_MODE_OPTIONS, modeHint, modeLabel, loadAgentMode, saveAgentMode,
} from '@/cubus/services/agentMode'
import { AI_MODE_OPTIONS, useAiOf, waitingNameOf } from '@/cubus/services/aiModes'
import {
  loadAttachmentsConfig, uploadAttachments, acceptOf, humanSize,
} from '@/cubus/services/agentAttachments'
import {
  resultFilesOf, failedFilesOf, pendingFilesOf, downloadAgentResult, resultSlotsOf,
} from '@/cubus/services/agentResultFiles'

// Настройка одного человека на одном браузере: делить её между устройствами незачем.
const PANEL_KEY = 'cubus.agent.panel'
const MODES = ['off', 'overlay', 'docked']
const DEFAULT_WIDTH = 380
const MIN_WIDTH = 280
// Потолок считается от окна, а не константой: на узком экране «рядом» иначе съедает страницу,
// ради которой панель и открывали.
const maxWidth = () => Math.max(MIN_WIDTH, Math.min(760, window.innerWidth - 480))
// Панель — не место для бесконечной ленты: она живёт весь сеанс работы в интерфейсе.
const LOG_LIMIT = 300
// BUG-635: сколько последних сообщений переписки поднимать при подключении (столько же отдаёт сервер по умолчанию).
const HISTORY_LIMIT = 50

export default {
  name: 'CbsAgentPanel',
  components: {
    BAvatar, BButton, BFormTextarea, BFormSelect, BFormInput, BModal,
  },
  data() {
    return {
      // PROP-754: подтверждение моста — состояние загружается заранее (окно моста открывается в том же нажатии)
      bridgeConsent: { required: false, variant: 'B', orgName: '' },
      bridgeConsentOpen: false,
      bridgeConsentChecked: false,
      bridgeConsentReason: 'connect',
      // язык ТЕКСТА подтверждения — выбирается в окне (KK/RU/EN), по умолчанию — язык интерфейса
      bridgeConsentLangSel: '',
      consentLangs: CONSENT_LANGS,
      DEFAULT_WIDTH,
      mode: 'off',
      lastOpen: 'docked',
      width: DEFAULT_WIDTH,
      resizing: false,
      messages: [],
      text: '',
      waiting: false,
      // PROP-401: по умолчанию взведено; снимается только явным запретом в запросе («не
      // открывай»). На первом ответе по существу: ровно одна внутренняя ссылка — открываем.
      autoOpenArmed: false,
      // Предохранитель ожидания: команда исполняется мгновенно, агент отвечает минутами, но
      // «думает» без конца — это поломка. Пусть лучше индикатор погаснет раньше времени, чем
      // висит вечно (ровно этим `/clear` и выглядел как ушедший к модели).
      waitingTimer: null,
      // PROP-408: счётчик ожидания в пузыре «Агент думает». `waitingSince` ставится один раз на
      // отправку и НЕ переставляется служебными строками — иначе счётчик прыгал бы к нулю на
      // каждой вехе и врал бы про то, сколько человек уже ждёт.
      waitingSince: 0,
      waitingElapsed: 0,
      waitingTicker: null,
      commandIndex: 0,
      commandsDismissed: false,
      status: 'offline',
      socket: null,
      // BUG-635: историю переписки панель запрашивает один раз — при первом подключении. После разрыва
      // лента уже в памяти, а места «принимается» догоняет `refreshPendingSlots`.
      historyAsked: false,
      // PROP-393: голосовой ввод. Распознаватель живёт только на время записи.
      isRecording: false,
      recognition: null,
      // RFILE-590: состояние скачивания по номеру файла — `{ busy, error }`. По номеру, а не по
      // сообщению: склейка присылает ОДИН файл в нескольких ответах, и «скачиваю…» должно гореть
      // на всех его кнопках сразу, а не только на той, что нажали.
      fileState: {},
      // PROP-395: история отправленных команд для стрелки «вверх» (как в терминале).
      commandHistory: [],
      historyIndex: -1,
      // PROP-426: листаем ли историю прямо сейчас. Пока не листаем, стрелки — про курсор.
      historyBrowsing: false,
      // Подстановку из истории отличаем от правки человека: иначе наблюдатель за текстом
      // сбрасывал бы листание на первом же шаге.
      fromHistory: false,
      // PROP-433: открыты ли настройки. По умолчанию закрыты и запоминаются вместе с остальными
      // настройками панели: кто их открыл — открыл осознанно, и на следующем экране они не
      // должны схлопываться сами.
      settingsOpen: false,
      // PROP-396: режим «Чат / Отладка». Отладка показывает служебные строки хода работы.
      // Значения те же, что на странице чат-бота (`chat`/`debug`).
      // По умолчанию «Отладка» — указание владельца 2026-09-10. В хранилище режим не ложится
      // (`save()` его не пишет), поэтому значение здесь и есть то, что человек видит при каждом
      // открытии панели: чтобы вернуть тихий «Чат», его надо выбрать заново.
      chatMode: 'debug',
      chatModeOptions: [
        { value: 'chat', text: 'Чат' },
        { value: 'debug', text: 'Отладка' },
      ],
      // PROP-402: режим подачи ответа. В «Презентации» агенту велят оформить ответ
      // самостоятельной HTML-страницей, а мы показываем её в основной области (маршрут
      // /cubus/presentation), в ленту кладём метку и резюме.
      // По умолчанию «Чат» — указание владельца 2026-09-23 (с 2026-09-10 было «Презентация»):
      // ответ обычным текстом, страница — только по явному выбору. Флаг едет полем кадра
      // `present` (см. `send`); просьбы открыть объект `wantsOpenObject` гасит и в презентации.
      presentMode: 'chat',
      presentModeOptions: [
        { value: 'chat', text: 'Чат' },
        { value: 'present', text: 'Презентация' },
      ],
      // Взводится на отправке в режиме презентации; гасится первым ответом по существу.
      presentArmed: false,
      // PROP-419: чей агент отвечает.
      // PROP-424: второй вариант РАЗБЛОКИРОВАН по указанию владельца — выбрать его можно.
      // ⚠️ Работающим он от этого не стал: проводки к локальному Клоду ещё нет, отвечает
      // по-прежнему демонстрационный. Поэтому на выборе показываем служебную строку — молча
      // принять выбор и продолжить отвечать другим агентом значило бы соврать переключателем.
      agentKind: 'demo',
      // PROP-656: кому разрешено отвечать. Умолчание прежнее — агент: панель до этого ходила
      // только к нему, и человек, не трогавший настройки, не должен заметить перемены.
      aiMode: 'agent',
      aiModeOptions: AI_MODE_OPTIONS,
      // PROP-468: режим работы агента; `null` — домен раскатан до PROP-464, режима там нет.
      agentMode: null,
      // FILE-516: признак и пределы прикрепления из whoami (null — недоступно), прикреплённые файлы.
      attachCfg: null,
      // BFILE-558: у локального Клода свой признак и свои пределы — выключатель моста, не раннера.
      attachLocalCfg: null,
      // PROP-813: свой ИИ организации (whoami.ownai); выключен — «Локальный Клод» не предлагается
      ownAi: true,
      attachments: [],
      attachUploading: false,
      agentModeOptions: AGENT_MODE_OPTIONS,
      agentKindOptions: [
        { value: 'demo', text: 'Демонстрационный' },
        { value: 'local', text: 'Локальный Клод' },
      ],
      // BRG-415: состояние моста. Адрес помним (не секрет); токена здесь нет — он живёт в
      // он даёт право выполнять команды на этой машине.
      bridgeUrl: getBridgeUrl(),
      bridgeSession: '',
      // BUG-643: чья сессия — адрес моста и папка, при которых она начата (`bridgeSessionOwnerKey`).
      bridgeSessionOwner: '',
      bridgeConnected: false,
      bridgeFolder: '',
      bridgeExpiresAt: 0,
      bridgeBusy: false,
      bridgeStatus: 'unknown',
      bridgeError: '',
      bridgeUnsubscribe: null,
      // BRG-432: видели ли мы, что инструменты Cubus доехали до агента.
      bridgeToolsOk: false,
      // BRG-437: разрешил ли их МОСТ — то есть несёт ли грант адрес инструментов.
      bridgeToolsAllowed: false,
      // BRG-441: раскрыта ли инструкция по установке и скопирована ли команда.
      installOpen: false,
      // BRG-441 (Windows): какая инструкция показана. Умолчание — по системе пользователя;
      // переключатель в панели это перекрывает. Linux и прочее ведём как macOS (shell-путь).
      installOs: /win/i.test(`${navigator.userAgent || ''} ${navigator.platform || ''}`) ? 'win' : 'mac',
      installCopied: false,
      installCopyFailed: false,
      // BRG-434: вопрос локальному Клоду отправлен, ответа ещё нет.
      bridgeAsking: false,
      // BRG-435: номер текущего запроса — по нему и только по нему его можно прервать.
      bridgeRequestId: '',
      // BFILE-559: номер прогона, выданный Cubus стартом; пусто — старый бэкенд, номера нет.
      bridgeJobId: '',
      // BFILE-559: описания файлов ЭТОГО прогона, подтверждённые сервером (отпечаток и размер).
      bridgeRunFiles: [],
      // BFILE-564: платформа разрешила этому прогону память (`local/start` → `memory: true`).
      bridgeMemory: false,
      // RFILE-630: признак файлов результата из `local/start` этого прогона.
      bridgeResultFeature: null,
      // RFILE-630: подсказка «свяжите заново / обновите мост», когда платформа файлы результата даёт, а мост нет.
      bridgeResultHint: '',
      bridgeCancelling: false,
      // PROP-456: чем агент пользовался, отвечая на ТЕКУЩИЙ вопрос. Копится по ходу работы из
      // аргументов вызовов и показывается сводкой под ответом — в «Чате», где служебных строк нет.
      answerSources: [],
    }
  },
  computed: {
    /** PROP-754: язык текста подтверждения — выбранный в окне; не выбран — язык интерфейса (en/kk), иначе русский. */
    bridgeConsentLang() {
      if (this.bridgeConsentLangSel) return this.bridgeConsentLangSel
      const l = this.$i18n ? String(this.$i18n.locale || '') : ''
      if (l.startsWith('en')) return 'en'
      if (l.startsWith('kk') || l.startsWith('kz')) return 'kk'
      return 'ru'
    },
    bridgeConsentText() {
      return bridgeConsentText(this.bridgeConsentLang, this.bridgeConsent.variant, this.bridgeConsent.orgName)
    },
    /** PROP-468: подпись текущего режима работы агента. */
    attachAccept() {
      return acceptOf(this.agentKind === 'local' ? this.attachLocalCfg : this.attachCfg)
    },
    /**
     * BFILE-558 (ТЗ моста §13 п. 2): скрепка у локального Клода — ТРИ условия сразу.
     *
     * Признак в `whoami` (выключатель моста, роль памяти, процедура следа в домене), возможность
     * `files` у моста и разрешение `files: true` в гранте. Мост сообщает возможность только при
     * разрешении, но это РАЗНЫЕ данные: сведи их в одно — и первое же расхождение покажет
     * человеку скрепку, которая отвечает отказом.
     */
    canAttach() {
      if (this.agentKind !== 'local') return !!this.attachCfg
      return !!this.attachLocalCfg && this.bridgeConnected && bridgeCanFiles()
    },
    /**
     * Мост подключён, платформа файлы разрешает, а мост их не умеет. Номер сборки называем: с ним
     * человеку есть что сказать, если обновление не помогло.
     */
    bridgeUpdateHint() {
      if (this.agentKind !== 'local' || !this.attachLocalCfg || !this.bridgeConnected) return ''
      if (bridgeCanFiles()) return ''
      /**
       * ⚠️ Причины ТРИ, и путать их нельзя: «обновите мост» человеку, который просто не дал
       * разрешения, — это совет, который не поможет, и он решит, что сломано (поймано браузерной
       * приёмкой). Отличаем по тому, что мост о себе рассказал.
       */
      const build = bridgeBuild()
      if (!build) {
        return 'Этот мост старый и про файлы ничего не сообщает — обновите его: claude-bridge update.'
      }
      if (!bridgeFilesAllowed()) {
        return 'Передача файлов этому доступу не разрешена — нажмите «Отключить», подключитесь заново '
          + 'и разрешите её вторым вопросом в окне согласия.'
      }
      return `Этот мост файлы передавать не умеет — обновите его: claude-bridge update (сборка ${build}). `
        + 'Если мост выставлен наружу, передача файлов в этом режиме выключена.'
    },
    agentModeLabel() {
      return modeLabel(this.agentMode)
    },
    /**
     * ⚠️ Про «все ИИ в этом домене» сказано намеренно: режим — свойство ПОЛЬЗОВАТЕЛЯ, а не
     * вкладки, и переключение здесь меняет его и локальному Клоду, и Cursor.
     */
    agentModeTitle() {
      return `Режим работы агента. ${modeHint(this.agentMode)} `
        + 'Настройка общая для всех ИИ, работающих в этом домене под вашей учётной записью.'
    },
    session() { return this.$store.getters['cubus-store/SESSION'] },
    isSpeechSupported() {
      return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
    },
    /**
     * BRG-434: ввод заперт, пока локальный Клод отвечает.
     *
     * Мост это ограничение держит и сам — повтор в той же сессии он отбивает `409` за 1 мс, — но
     * сюда дело не доходит: `bridgeSession` приходит ВМЕСТЕ с ответом, и до него второй запрос
     * ушёл бы БЕЗ номера сессии, то есть новым разговором. Агент не увидел бы предыдущего
     * вопроса, а ветка молча подменилась бы той, чей ответ пришёл последним.
     *
     * Только для локального Клода: у серверного агента разговор ведёт бэкенд, и очереди на одну
     * машину там нет.
     */
    inputLocked() { return this.agentKind === 'local' && this.bridgeAsking },
    agentKindOptionsShown() { return this.ownAi ? this.agentKindOptions : this.agentKindOptions.filter(o => o.value !== 'local') },
    // PROP-408: сколько идёт ожидание, секундомером `м:сс` — привычная форма, читается с одного
    // взгляда и не прыгает по ширине после первой минуты.
    // PROP-656: кто думает — по выбранному режиму; локальный Клод — тоже агент.
    waitingWho() {
      return this.agentKind === 'local' ? 'Агент' : waitingNameOf(this.aiMode)
    },
    waitingLabel() {
      const s = Math.max(0, this.waitingElapsed)
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    },
    // PROP-397: «Чат» показывает только значимые сообщения — реплики. Служебные строки
    // (показания Debug, вехи хода работы, передача управления) видны только в «Отладке».
    visibleMessages() {
      if (this.chatMode === 'debug') return this.messages
      return this.messages.filter(m => !m.service)
    },
    /**
     * Панель не показывается двум категориям экранов.
     *
     * Без сессии — очевидно: чат требует токена, а на странице входа его ещё нет.
     * На самой странице чат-бота — потому что там этот же разговор уже идёт в полный экран:
     * две ленты одной переписки на одном экране спорят друг с другом, а сообщения сервер шлёт
     * во ВСЕ соединения пользователя, и ответ дублировался бы.
     */
    visible() {
      return !!(this.session && this.session.token) && this.$route.path !== '/cubus/chatbot'
    },
    /** Команды после «/» — тот же реестр, что и на странице чата (PROP-384). */
    commandHints() {
      return this.commandsDismissed ? [] : matchCommands(this.text)
    },
    statusLabel() {
      if (this.status === 'online') return 'на связи'
      return this.status === 'connecting' ? 'подключаюсь…' : 'нет связи'
    },
    /**
     * BRG-442: состояние ВЫБРАННОГО агента — то, что человек проверяет взглядом перед тем,
     * как писать.
     *
     * У серверного агента признак верный — состояние сокета. У локального он не значит ничего:
     * сокет к Cubus жив всегда, а мост может быть не установлен, не запущен, не связан либо
     * связан, но Claude Code в нём не готов. Поэтому для локального берём состояние моста,
     * которое панель и так знает.
     */
    agentStatusLabel() {
      if (this.agentKind !== 'local') return this.statusLabel
      if (this.bridgeConnected) {
        if (this.bridgeStatus === 'notready') return 'Клод не готов'
        return this.bridgeStatus === 'ready' ? 'на связи' : 'подключён'
      }
      return {
        checking: 'проверяю…',
        connecting: 'подключаюсь…',
        offline: 'мост не отвечает',
      }[this.bridgeStatus] || 'не подключён'
    },
    /** Тон той же надписи: зелёный — можно писать, жёлтый — идёт, красный — писать некому. */
    agentTone() {
      if (this.agentKind !== 'local') {
        if (this.status === 'online') return 'ok'
        return this.status === 'connecting' ? 'wait' : 'bad'
      }
      if (this.bridgeConnected) return this.bridgeStatus === 'ready' ? 'ok' : 'wait'
      return ['checking', 'connecting'].includes(this.bridgeStatus) ? 'wait' : 'bad'
    },
    // PROP-425: состояние моста словами. «Мост жив» и «Клод готов работать» — разные вещи, и
    // разделены они намеренно: чаще всего мост поднят, а вход в Claude Code не выполнен.
    bridgeStatusLabel() {
      return {
        checking: 'проверяю…',
        idle: 'не подключён',
        connecting: 'подключение…',
        connected: 'подключён',
        ready: 'мост готов',
        notready: 'мост есть, Клод не готов',
        offline: 'мост не отвечает',
      }[this.bridgeStatus] || ''
    },
    /**
     * BRG-441: строка установки, своя под ОС (см. installOs). Ни токена, ни адреса заказчика
     * в ней нет: токен рождается на машине при первом запуске, доступ выдаётся связыванием.
     */
    installCommand() {
      // BRG-441 (Windows): на Windows готовой поставки-службы пока нет — сборка из исходников.
      // Многострочная команда: копируется целиком, а в коде выводится с переносами (white-space:
      // pre). Node ставится отдельным шагом выше — здесь только клон, сборка и запуск.
      if (this.installOs === 'win') {
        // Windows-поддержка влита в main моста (ba12b1b) — берём обычный clone без ветки.
        return [
          'git clone https://gitlab.com/cubus_project/cubus-claude-bridge.git',
          'cd cubus-claude-bridge',
          'npm ci',
          'npm run build',
          'npm start',
        ].join('\n')
      }
      return 'curl -fsSL https://get.cubus.kz/bridge/install.sh | sh'
    },
    /** До какого времени выдан доступ. Дата человеку не нужна — грант живёт часы. */
    bridgeUntil() {
      if (!this.bridgeExpiresAt) return ''
      return new Date(this.bridgeExpiresAt)
        .toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    },
  },
  watch: {
    // PROP-754: состояние подтверждения моста — только когда выбран локальный агент. Панель живёт и на странице
    // входа; запрос без сессии (401) перехватчик превращает в «сессия завершена», и вход перезагружался по кругу.
    agentKind(v) {
      if (v === 'local') this.loadBridgeConsent()
    },
    // BRG-441: мост не отвечает — самое время показать, как его завести. Свернуть человек
    // может сам; обратно не закрываем, иначе подсказка исчезала бы прямо под рукой.
    bridgeStatus(value) {
      if (value === 'offline') this.installOpen = true
    },
    text() {
      this.commandsDismissed = false
      this.commandIndex = 0
      // PROP-426: человек начал править — листание истории закончилось. Иначе следующая
      // стрелка снова затёрла бы уже изменённый текст.
      if (!this.fromHistory) {
        this.historyBrowsing = false
        this.historyIndex = -1
      }
    },
    visible(now) {
      if (now && this.mode !== 'off') this.connect()
      if (!now) this.disconnect()
      this.applyDocked()
    },
    mode() { this.applyDocked() },
    width() { this.applyDocked() },
  },
  mounted() {
    this.restore()
    // Соединение с мостом живёт в сервисе, а не в панели: окно могут закрыть, а доступ —
    // отозвать из самого окна. Панель должна узнать об этом, а не показывать «подключено».
    this.bridgeUnsubscribe = onBridgeChange((c, why) => {
      this.applyBridgeConnection(c)
      // Сознательное «Отключить» — не поломка, и говорить о нём как об ошибке нельзя.
      if (!c && why !== 'manual') {
        this.bridgeError = why === 'closed'
          ? 'Окно моста закрыто — доступ прекращён.'
          : 'Доступ к папке отозван.'
      }
    })
    window.addEventListener('resize', this.onWindowResize)
    this.loadAgentMode()
    this.loadAttachCfg()
    if (this.visible && this.mode !== 'off') this.connect()
    this.applyDocked()
  },
  beforeDestroy() {
    if (this.bridgeUnsubscribe) this.bridgeUnsubscribe()
    window.removeEventListener('resize', this.onWindowResize)
    clearTimeout(this.waitingTimer)
    clearInterval(this.waitingTicker)
    if (this.recognition) {
      this.recognition.abort()
      this.recognition = null
    }
    this.disconnect()
    this.applyDocked(true)
  },
  methods: {
    // --- настройка -----------------------------------------------------------
    restore() {
      let saved = null
      try {
        saved = JSON.parse(localStorage.getItem(PANEL_KEY) || 'null')
      } catch (e) {
        // В приватном окне и при запрещённых куках обращение к хранилищу БРОСАЕТ, а не
        // возвращает пусто: без перехвата панель роняла бы весь интерфейс ради своей ширины.
        saved = null
      }
      if (!saved || typeof saved !== 'object') return
      // Значения из хранилища недоверенные — их правит кто угодно через консоль.
      if (MODES.includes(saved.mode)) this.mode = saved.mode
      if (MODES.includes(saved.lastOpen) && saved.lastOpen !== 'off') this.lastOpen = saved.lastOpen
      else if (MODES.includes(saved.mode) && saved.mode !== 'off') this.lastOpen = saved.mode
      this.width = this.clamp(Number(saved.width) || DEFAULT_WIDTH)
      // PROP-433: только явное `true`. Отсутствие ключа — это «настройка не выбиралась», и она
      // должна означать закрыто, как у всех, кто панель ещё не трогал.
      this.settingsOpen = saved.settingsOpen === true
      this.narrowGuard()
    },
    save() {
      try {
        localStorage.setItem(PANEL_KEY, JSON.stringify({
          mode: this.mode, lastOpen: this.lastOpen, width: this.width, settingsOpen: this.settingsOpen,
        }))
      } catch (e) { /* хранилище недоступно — настройка не переживёт перезагрузку */ }
    },
    toggleSettings() {
      this.settingsOpen = !this.settingsOpen
      this.save()
    },
    clamp(px) { return Math.min(maxWidth(), Math.max(MIN_WIDTH, Math.round(px))) },
    setWidth(px) { this.width = this.clamp(px); this.save() },
    setMode(mode) {
      if (!MODES.includes(mode)) return
      this.mode = mode
      if (mode !== 'off') {
        this.lastOpen = mode
        this.connect()
      }
      this.width = this.clamp(this.width)
      this.save()
    },
    toggle() { this.setMode(this.mode === 'off' ? this.lastOpen : 'off') },

    /**
     * Режим «рядом» должен сжимать СТРАНИЦУ, а панель при этом фиксированная. Ширина уходит
     * в CSS-переменную на корне документа, а само сужение делает одно глобальное правило в
     * стилях этого компонента: так вендорный шаблон (`@core`) остаётся нетронутым — его
     * разметку править нельзя, а класс на `body` он переживёт.
     */
    applyDocked(off = false) {
      const docked = !off && this.visible && this.mode === 'docked'
      document.documentElement.style.setProperty('--cbs-agent-width', `${this.width}px`)
      document.body.classList.toggle('cbs-agent-docked', docked)
    },
    narrowGuard() {
      if (this.mode === 'docked' && window.innerWidth < 1024) this.mode = 'overlay'
      this.width = this.clamp(this.width)
    },
    onWindowResize() { this.narrowGuard(); this.applyDocked() },
    startResize(event) {
      const startX = event.clientX
      const startWidth = this.width
      this.resizing = true
      // Слушатели на документе: курсор уходит с узкой полоски за первые же миллиметры, и
      // обработчик, висящий на самом ползунке, теряет тягу на середине движения.
      const onMove = e => { this.width = this.clamp(startWidth - (e.clientX - startX)) }
      const onUp = () => {
        this.resizing = false
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.body.style.userSelect = ''
        this.save()
      }
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },

    /**
     * Что человек ВИДИТ в момент вопроса (PROP-390).
     *
     * «Покажи не удалённые» без этого — вопрос ни о чём: агент не знает, какая таблица открыта,
     * и либо переспрашивает, либо угадывает. Снимок берётся из адреса страницы и её заголовка:
     * это то, что панель знает НАВЕРНЯКА, не заглядывая в чужие компоненты. Заглядывать туда
     * (какие строки видны, какие фильтры стоят) — следующий шаг, и он потребует от страниц
     * договорённости; выдумывать её задним числом за них нельзя.
     *
     * `source` важен отдельно: из панели спрашивают ПРО ЭКРАН, со страницы чата — вообще.
     */
    viewContext() {
      const r = this.$route || {}
      const view = {
        source: 'sidebar',
        path: r.path || '',
        page: r.name || '',
        // Заголовок ставит сама страница после загрузки — по нему видно ИМЯ открытого объекта,
        // а не только его номер.
        title: (document.title || '').trim(),
      }
      const p = r.params || {}
      // Ключ объекта в адресе может быть и числом, и sid (BUG-387) — отдаём как есть, разбирать
      // его дело того, кто будет спрашивать платформу.
      if (p.objectid) view.object = String(p.objectid)
      if (p.object) view.object = String(p.object)
      if (p.record) view.record = String(p.record)
      return view
    },

    // --- разговор ------------------------------------------------------------
    connect() {
      if (this.socket) return
      this.socket = openChatSocket({
        token: () => (this.session && this.session.token) || '',
        onStatus: s => {
          // RFILE-632: сокет снова открыт после разрыва — места, закрытые за это время, спросить явно.
          const reopened = s === 'online' && this.status !== 'online'
          this.status = s
          if (reopened) this.refreshPendingSlots()
        },
        onMessage: this.onServerMessage,
      })
    },
    /**
     * RFILE-632 (ТЗ «Мост: файлы результата» §7 п. 3): пузыри с «принимается» — состояние мест через
     * `result-slots` и замена полей, как кадром. Годится и ответам раннера: историю панель не перечитывает.
     */
    async refreshPendingSlots() {
      const jobIds = [...new Set(this.messages
        .filter(m => m.jobid && Array.isArray(m.pendingFiles) && m.pendingFiles.length > 0)
        .map(m => m.jobid))]
      if (jobIds.length === 0) return
      const jobs = await resultSlotsOf(jobIds)
      jobs.forEach(job => this.applyFilesFrame(job))
    },
    /**
     * BUG-635: переписка с агентом живёт на сервере (`chat:<sid>:chatbot`), а панель держала её только в памяти
     * вкладки — после перезагрузки лента была пустой, и файл результата пропадал из вида.
     */
    askHistory() {
      if (!this.socket) return
      this.historyAsked = true
      this.socket.send({ type: 'history', with: 'chatbot', limit: HISTORY_LIMIT })
    },
    /**
     * BUG-635: история — в начало ленты, раньше живых сообщений. Ответ мог прийти ПОСЛЕ живого сообщения,
     * которое тоже уже лежит в истории, — такое пропускается по совпадению текста и времени.
     * Презентация из истории не открывается: человек её не просил, в ленте остаётся резюме.
     */
    applyHistory(data) {
      const list = Array.isArray(data.messages) ? data.messages : []
      // Подпись вложений в ленте — именами, в истории — числом: сравнивается текст до неё.
      const base = t => String(t || '').split('\n\n📎')[0]
      const same = (a, b) => a.sender === b.sender && base(a.text) === base(b.text)
        && Math.abs(new Date(a.at).getTime() - new Date(b.at).getTime()) < 10000
      const older = list
        .filter(d => d && d.type === 'message' && (d.text || d.page || d.files || d.files_failed))
        .map(d => this.historyBubble(d))
        .filter(h => !this.messages.some(m => same(m, h)))
      if (older.length === 0) return
      const next = [...older, ...this.messages]
      this.messages = next.length > LOG_LIMIT ? next.slice(next.length - LOG_LIMIT) : next
      this.$nextTick(() => {
        const box = this.$refs.log
        if (box) box.scrollTop = box.scrollHeight
      })
      this.refreshPendingSlots()
    },
    historyBubble(d) {
      const at = new Date(d.ts || Date.now())
      if (d.from !== 'chatbot') {
        const n = Array.isArray(d.attachments) ? d.attachments.length : 0
        return {
          sender: 'user', author: 'Вы', at, text: n ? `${d.text || ''}\n\n📎 вложений: ${n}` : (d.text || ''),
        }
      }
      let text = d.text || ''
      if (d.page) {
        const fromText = extractPresentation(text)
        const summary = (fromText && fromText.summary) || text.trim()
        text = `${summary || 'Готово.'}\n\n_Ответ был презентацией — после перезагрузки страница не сохраняется._`
      }
      return {
        sender: 'bot',
        text,
        author: d.fromName || 'Агент',
        at,
        service: d.transient === true || d.kind === 'service' || d.kind === 'pending',
        files: resultFilesOf(d.files),
        filesFailed: failedFilesOf(d.files_failed),
        pendingFiles: pendingFilesOf(d.files),
        jobid: d.jobid ? String(d.jobid) : '',
      }
    },
    disconnect() {
      if (this.socket) { this.socket.close(); this.socket = null }
      this.status = 'offline'
    },
    onServerMessage(data) {
      // RFILE-613: места файлов ответа изменились — заменить поля пузыря (ТЗ «Фоновая приёмка» §8 п. 3).
      if (data.type === 'files') {
        this.applyFilesFrame(data)
        return
      }
      // BUG-635: историю — после подтверждения входа: кадры до `auth` сервер отбрасывает.
      if (data.type === 'connected') {
        if (!this.historyAsked) this.askHistory()
        return
      }
      if (data.type === 'history') {
        if (data.with === 'chatbot') this.applyHistory(data)
        return
      }
      if (data.type === 'message') {
        // Служебная строка и «передал агенту» приходят ПЕРЕД ответом: гасить ожидание по ним
        // значит оставить человека ждать настоящий ответ в тишине (урок PROP-355).
        const pending = data.kind === 'service' || data.kind === 'pending'
        if (pending) this.startWaiting()
        else this.stopWaiting()
        const isReply = !(data.transient === true || data.kind === 'service' || data.kind === 'pending')
        // PROP-402: ответ-презентация. На первом ответе по существу пробуем достать html-страницу;
        // получилось — показываем её в основной области, в ленту кладём метку и резюме, а СЫРОЙ html
        // в пузырь не пишем. Взвод одноразовый. Не разобрали (агент не прислал страницу) — степенная
        // деградация: обычная реплика ниже.
        if (this.presentArmed && isReply) {
          this.presentArmed = false
          // PROP-423: страница приходит ОТДЕЛЬНЫМ блоком `page`. Разбор текста остался
          // запасным путём — раннер выкатывается отдельно от фронта, и в промежутке между
          // выкатами ответы приходят по-старому.
          // ⚠️ Даже когда блок есть, текст всё равно прогоняем через разбор: раннер намеренно
          // НЕ вырезает разметку из ответа (её ждут установки постарше), и без этого вся
          // страница целиком уехала бы в пузырь переписки вместо короткого резюме.
          const fromText = extractPresentation(data.text)
          // Разметки в тексте не было (страница пришла из прозы ответа) — тогда весь текст и
          // есть резюме: терять содержательный ответ ради заглушки «Готово.» нельзя.
          const page = data.page
            ? { html: data.page, summary: (fromText && fromText.summary) || (data.text || '').trim() }
            : fromText
          if (page) {
            // BUG-429: ссылка ведёт на ЭТУ страницу, а не «на презентацию вообще». Номер даёт
            // хранилище; без него все ссылки в переписке вели в одно место, и у старого ответа
            // открывался чужой, самый свежий результат.
            const pageId = setPresentation(page.html)
            const href = `/cubus/presentation?p=${pageId}`
            if (this.$router.currentRoute.fullPath !== href) {
              this.$router.push(href).catch(() => {})
            }
            const summary = page.summary || 'Готово.'
            this.push({
              sender: 'bot',
              text: `${summary}\n\n_Презентация открыта в основной области — [открыть снова](${href})._`,
              author: data.fromName || 'Агент',
              at: data.ts,
              // RFILE-590: ответ-презентация тоже может принести файлы — терять их из-за того,
              // что ответ ушёл в основную область, нельзя.
              files: resultFilesOf(data.files),
              filesFailed: failedFilesOf(data.files_failed),
              pendingFiles: pendingFilesOf(data.files),
              jobid: data.jobid ? String(data.jobid) : '',
            })
            return
          }
        }
        this.push({
          sender: 'bot',
          text: data.text,
          author: data.fromName || 'Агент',
          at: data.ts,
          // Передача управления («передал агенту», kind==='pending') — тоже служебная строка,
          // а не реплика: в «Чате» её не показываем (PROP-397).
          service: data.transient === true || data.kind === 'service' || data.kind === 'pending',
          // RFILE-590: файлы результата и отказы — полями сообщения (ТЗ §8 п. 1). Разбор здесь же
          // отсеивает недоверенное: кнопка без номера или без имени не рисуется.
          files: resultFilesOf(data.files),
          filesFailed: failedFilesOf(data.files_failed),
          // RFILE-613: места «принимается» и адрес пузыря для кадра `files`.
          pendingFiles: pendingFilesOf(data.files),
          jobid: data.jobid ? String(data.jobid) : '',
        })
        // PROP-400: ответ по существу на запрос «открой …». Ровно одна внутренняя ссылка в нём —
        // открываем сами. Взвод одноразовый: снимаем на первом ответе по существу (ноль или
        // несколько вариантов — выбор за человеком).
        if (this.autoOpenArmed && isReply) {
          this.autoOpenArmed = false
          // PROP-423: агент называет объект отдельным блоком `link` — гадать по тексту больше
          // не нужно. Разбор текста остаётся запасным путём на время, пока раннер старый.
          if (data.link) this.openInternalHref(data.link)
          else {
            const links = internalLinksOf(data.text)
            if (links.length === 1) this.openInternalHref(links[0])
          }
        }
        return
      }
      if (data.type === 'error') {
        // PROP-408: через stopWaiting, а не `waiting=false` — иначе секундомер продолжал бы
        // тикать в фоне после ошибки.
        this.stopWaiting()
        this.push({
          sender: 'bot', text: `Ошибка: ${data.text}`, author: 'Cubus', at: new Date(),
        })
      }
    },
    /**
     * RFILE-613: кадр `files` — ПОЛНЫЙ текущий список мест ответа `jobid`. Поля пузыря заменяются
     * целиком, а не дополняются: пропущенный кадр не оставляет расхождения. Пузыря нет (вытеснен
     * пределом ленты, панель открыта после ответа) — кадр пропускается. Своего таймера у панели нет:
     * срок держит сторож платформы и сам присылает отказ.
     */
    applyFilesFrame(data) {
      const jobid = data && data.jobid ? String(data.jobid) : ''
      if (!jobid) return
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        const m = this.messages[i]
        if (m.jobid === jobid) {
          this.$set(this.messages, i, {
            ...m,
            files: resultFilesOf(data.files),
            pendingFiles: pendingFilesOf(data.files),
            filesFailed: failedFilesOf(data.files_failed),
          })
          return
        }
      }
    },
    push(m) {
      this.messages.push({ ...m, at: m.at ? new Date(m.at) : new Date() })
      if (this.messages.length > LOG_LIMIT) this.messages.splice(0, this.messages.length - LOG_LIMIT)
      this.$nextTick(() => {
        const box = this.$refs.log
        if (box) box.scrollTop = box.scrollHeight
      })
    },
    send() {
      const text = this.text.trim()
      if (!text) return
      // BRG-434: набранное НЕ теряем и не отправляем — его просто рано слать. Возврат раньше
      // `this.text = ''`, поэтому текст остаётся в поле и уйдёт, когда придёт ответ.
      if (this.inputLocked || this.waiting) return
      // PROP-395: в историю — самое свежее первым; указатель сбрасывается на «набираю новое».
      this.commandHistory.unshift(text)
      this.historyIndex = -1
      this.historyBrowsing = false
      // FILE-516: вложения уходят вместе с этим сообщением и снимаются с поля ввода.
      const attached = this.agentKind === 'local' ? [] : this.attachments.slice()
      this.push({
        sender: 'user',
        text: attached.length ? `${text}\n\n📎 ${attached.map(a => a.name).join(', ')}` : text,
        author: 'Вы',
        at: new Date(),
      })
      this.text = ''
      if (attached.length) this.attachments = []
      // PROP-385: слэш — детерминированный канал и здесь. Часть команд исполняет клиент
      // (`/clear`, `/help`) — их нельзя отправлять «в разговор»: ждать по ним нечего, а
      // ожидание, которое не кончается, человек читает как «ушло к модели».
      if (this.handleCommand(text)) return
      // PROP-401: авто-открытие взведено ПО УМОЛЧАНИЮ; гасит его только явный запрет в запросе
      // («не открывай», «без открытия»). Локальные команды сюда уже не доходят — вышли выше.
      this.autoOpenArmed = !saysDoNotOpen(text)
      // PROP-402/403: режим презентации едет ШТАТНЫМ полем кадра `present`, а не инструкцией в
      // тексте: раннер намеренно не исполняет указания из содержимого сообщений (защита от
      // инъекций). Бэк проведёт флаг в конверт агента, а системный промпт раннера велит оформить
      // ответ HTML-страницей. Взводим разбор ответа на страницу; авто-открытие ссылки не нужно.
      //
      // PROP-407: но если просят ОТКРЫТЬ объект — страница не нужна, нужен сам объект. Тогда
      // презентацию не просим вовсе (`present:false`), а взвод авто-открытия остаётся: агент
      // ответит ссылкой, и её откроет `PROP-401`. Страницу генерим во всех остальных случаях.
      this.presentArmed = this.presentMode === 'present' && !wantsOpenObject(text)
      if (this.presentArmed) this.autoOpenArmed = false
      this.startWaiting()
      // PROP-425: локальный Клод отвечает МИМО бэкенда — запрос идёт с этой страницы прямо на
      // мост, поднятый у человека на машине. Поэтому здесь другая ветка целиком: ни сокета, ни
      // конверта задания, ни истории на сервере.
      if (this.agentKind === 'local') { this.sendToBridge(text); return }
      const frame = {
        type: 'message',
        to: 'chatbot',
        text,
        aiMode: this.aiMode,
        useAi: useAiOf(this.aiMode),
        mode: this.chatMode,
        present: this.presentArmed,
        view: this.viewContext(),
        attachments: attached.map(a => a.id),
        token: (this.session && this.session.token) || '',
      }
      this.deliver(frame).then(sent => {
        if (sent) return
        this.stopWaiting()
        this.push({
          sender: 'bot',
          author: 'Cubus',
          at: new Date(),
          text: 'Нет связи с сервером — сообщение не отправлено. Попробуйте ещё раз, когда связь вернётся.',
        })
      })
    },

    /**
     * Отправить кадр, ДОЖДАВШИСЬ соединения.
     *
     * Панель подключается в момент открытия, и первый вопрос человек задаёт раньше, чем сокет
     * успевает открыться: сообщение гибло с «нет связи» на исправном сервере. Ждать секунды
     * правильнее, чем отвечать отказом, — соединение к этому времени уже есть.
     *
     * Ждём ограниченно: если связи действительно нет, «отправляю…» без конца хуже честного
     * отказа (тот же урок, что с висящим ожиданием в BUG-385).
     */
    async deliver(frame, waitMs = 4000) {
      const deadline = Date.now() + waitMs
      while (Date.now() < deadline) {
        if (this.socket && this.socket.isOpen()) return this.socket.send(frame)
        if (!this.socket) this.connect()
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, 200))
      }
      return false
    },

    /** Команды, на которые отвечает сам клиент. Возвращает true, если ответила панель. */
    handleCommand(text) {
      const parsed = parseCommand(text)
      if (!parsed.handled) return false
      if (parsed.localReply === 'clear') {
        this.messages = []
        this.push({
          sender: 'bot', author: 'Cubus', at: new Date(), text: '🧹 Контекст очищен.',
        })
        // Серверу тоже надо: история разговора живёт у него, и очистить только окно значило бы
        // соврать — следующий вопрос агент продолжил бы со старым контекстом.
        if (this.socket) {
          this.socket.send({
            type: 'message',
            to: 'chatbot',
            text: '/clear',
            silent: true,
            mode: 'chat',
            token: (this.session && this.session.token) || '',
          })
        }
        return true
      }
      if (parsed.localReply === 'help') {
        this.push({
          sender: 'bot', author: 'Cubus', at: new Date(), text: HELP_TEXT,
        })
        return true
      }
      if (parsed.localReply) {
        this.push({
          sender: 'bot', author: 'Cubus', at: new Date(), text: parsed.localReply,
        })
        return true
      }
      // Команда есть, но исполняет её платформа: отправляем то, что она понимает.
      if (parsed.serverText) {
        this.startWaiting()
        if (this.socket) {
          this.socket.send({
            type: 'message',
            to: 'chatbot',
            text: parsed.serverText,
            // PROP-388: наш перевод слэш-команды, а не текст человека.
            command: true,
            aiMode: this.aiMode,
            useAi: useAiOf(this.aiMode),
            mode: 'chat',
            token: (this.session && this.session.token) || '',
          })
        }
        return true
      }
      return false
    },

    startWaiting() {
      // PROP-408: отсчёт начинаем только при ПЕРЕХОДЕ в ожидание. Служебные строки зовут
      // startWaiting снова и снова — сбрасывая здесь, мы обнуляли бы счётчик на каждой вехе.
      if (!this.waiting) {
        this.waitingSince = Date.now()
        this.waitingElapsed = 0
        clearInterval(this.waitingTicker)
        this.waitingTicker = setInterval(() => {
          this.waitingElapsed = Math.floor((Date.now() - this.waitingSince) / 1000)
        }, 1000)
      }
      this.waiting = true
      clearTimeout(this.waitingTimer)
      // Потолок втрое больше самого долгого виденного ответа агента: предохранитель, а не
      // тайм-аут задания — задание живёт на сервере и придёт, даже если точки погасли.
      this.waitingTimer = setTimeout(() => { this.stopWaiting() }, 15 * 60 * 1000)
    },
    stopWaiting() {
      this.waiting = false
      clearTimeout(this.waitingTimer)
      clearInterval(this.waitingTicker)
      this.waitingTicker = null
    },

    pickCommand(c) {
      this.text = c.args ? `${c.cmd} ` : c.cmd
      this.$nextTick(() => { this.commandsDismissed = true })
    },
    // Стрелки: при ОТКРЫТОМ списке команд листают его (как раньше), при закрытом — историю
    // отправленных команд (PROP-395, как в терминале и на странице чат-бота). Две роли на одной
    // клавише не спорят: список открыт только когда строка начинается со «/», а история нужна на
    // пустой/обычной строке.
    onArrowUp(e) {
      if (this.commandHints.length) {
        e.preventDefault()
        this.commandIndex = (this.commandIndex - 1 + this.commandHints.length) % this.commandHints.length
        return
      }
      // PROP-418: в резиновом поле стрелка прежде всего двигает каретку между строками.
      // История поднимается только С ПЕРВОЙ строки — иначе многострочный текст стало бы
      // нечем править: первое же нажатие затирало бы набранное прошлой командой.
      if (!atFirstLine(e)) return
      this.historyUp(e)
    },
    onArrowDown(e) {
      if (this.commandHints.length) {
        e.preventDefault()
        this.commandIndex = (this.commandIndex + 1) % this.commandHints.length
        return
      }
      if (!atLastLine(e)) return
      this.historyDown(e)
    },
    // «Вверх» уводит в прошлое (к более старым), «вниз» возвращает к последней и к пустой строке.
    // `historyIndex` = -1 значит «набираю новое», 0 — последняя отправленная.
    //
    // ⚠️ PROP-426: история поднимается ТОЛЬКО из пустого поля либо когда её уже листают.
    // Жалоба владельца: набран многострочный запрос, «вверх» для правки строки выше — и весь
    // текст заменён прошлой командой, «вниз» очищает поле совсем. Проверки каретки (PROP-418)
    // от этого не спасали: они решают, ГДЕ стоит курсор, а не МОЖНО ЛИ терять набранное. На
    // первой строке (и в любой длинной строке, которую перенесло по ширине) курсор законно
    // стоит в начале — и правило «в начале листаем историю» стирало работу.
    //
    // Клавиша при непустом поле теперь просто двигает курсор: сначала не потерять набранное,
    // история — удобство второго порядка. Из пустого поля она работает как прежде.
    historyUp(e) {
      if (this.commandHistory.length === 0) return
      if (!this.historyBrowsing && this.text.trim() !== '') return
      if (this.historyIndex < this.commandHistory.length - 1) {
        e.preventDefault()
        this.historyIndex += 1
        this.setFromHistory(this.commandHistory[this.historyIndex])
      }
    },
    historyDown(e) {
      // Не листаем историю — «вниз» не наше дело: курсор должен ехать вниз по строкам.
      if (!this.historyBrowsing || this.historyIndex < 0) return
      e.preventDefault()
      if (this.historyIndex > 0) {
        this.historyIndex -= 1
        this.setFromHistory(this.commandHistory[this.historyIndex])
      } else {
        this.historyIndex = -1
        this.setFromHistory('')
        this.historyBrowsing = false
      }
    },
    /** Подстановка из истории: не считается правкой человека, иначе листание сбрасывало бы себя. */
    setFromHistory(value) {
      this.historyBrowsing = true
      this.fromHistory = true
      this.text = value
      this.$nextTick(() => { this.fromHistory = false })
    },
    onTab(e) {
      if (!this.commandHints.length) return
      e.preventDefault()
      this.pickCommand(this.commandHints[this.commandIndex])
    },
    // BRG-415: выбран локальный Клод — сразу говорим, в каком он состоянии. Молчаливое
    // переключение оставило бы человека выяснять это первым запросом, который просто не поедет.
    /** PROP-468: текущий режим. Нет поля в ответе — режим в этом домене не применяется. */
    /** FILE-516: признак прикрепления. Ошибка запроса — кнопки нет, а не «есть, но не работает». */
    async loadAttachCfg() {
      try {
        const cfg = await loadAttachmentsConfig()
        this.attachCfg = cfg.runner
        this.attachLocalCfg = cfg.local
        this.ownAi = cfg.ownAi !== false
        if (!this.ownAi && this.agentKind === 'local') this.agentKind = 'demo'
      } catch (e) {
        this.attachCfg = null
        this.attachLocalCfg = null
      }
    },
    fileSize(b) {
      return humanSize(b)
    },
    // --- файлы результата (RFILE-590) ------------------------------------------
    fileBusy(f) {
      return !!(this.fileState[f.id] && this.fileState[f.id].busy)
    },
    fileError(f) {
      return (this.fileState[f.id] && this.fileState[f.id].error) || ''
    },
    /**
     * Скачать файл результата. Отказ платформы показывается ПОД кнопкой, её же словами: у
     * `agentresult` один отказ на «нет», «не ваш» и «чужого происхождения», и пересказывать его
     * своими словами значило бы угадывать, какой из трёх случаев произошёл.
     */
    async downloadResultFile(f) {
      if (this.fileBusy(f)) return
      this.$set(this.fileState, f.id, { busy: true, error: '' })
      try {
        await downloadAgentResult(f)
        this.$set(this.fileState, f.id, { busy: false, error: '' })
      } catch (e) {
        this.$set(this.fileState, f.id, { busy: false, error: (e && e.message) || 'не удалось скачать' })
      }
    },
    /** FILE-516: выбранные файлы сразу загружаются; отказ сервера — сообщением в разговоре. */
    async onFilesChosen(e) {
      const input = e && e.target
      const chosen = input && input.files ? Array.from(input.files) : []
      if (input) input.value = ''
      if (!chosen.length) return
      this.attachUploading = true
      try {
        const saved = await uploadAttachments(chosen)
        const known = new Set(this.attachments.map(a => a.id))
        /**
         * BFILE-560: рядом с описанием от сервера держим САМ файл. Эти байты уже в браузере —
         * второй передачи по сети не нужно. Сопоставляем по имени и размеру: порядок ответа
         * сервера обещанием не является.
         */
        saved.filter(f => !known.has(f.id)).forEach(f => {
          const src = chosen.find(c => c.name === f.name && c.size === f.size) || null
          this.attachments.push({ ...f, file: src })
        })
      } catch (err) {
        this.push({
          sender: 'bot', author: 'Cubus', at: new Date(), text: `⚠️ Файл не прикреплён: ${err.message}`,
        })
      } finally {
        this.attachUploading = false
      }
    },
    removeAttachment(id) {
      this.attachments = this.attachments.filter(a => a.id !== id)
    },
    async loadAgentMode() {
      try {
        this.agentMode = await loadAgentMode()
      } catch (e) {
        this.agentMode = null
      }
    },

    /**
     * Переключение режима. О смене говорим В ПЕРЕПИСКЕ, а не молча: настройка общая для всех
     * ИИ этого человека, и её последствия он увидит в другом инструменте.
     */
    async onAgentModeChange(value) {
      const previous = this.agentMode
      try {
        this.agentMode = await saveAgentMode(value)
        this.push({
          sender: 'bot',
          author: 'Cubus',
          text: `_Режим агента: **${this.agentModeLabel}**. ${modeHint(this.agentMode)} `
            + 'Настройка действует на все ИИ, работающие в этом домене под вашей учётной записью._',
        })
      } catch (e) {
        // Список обязан показывать то же, что знает сервер, — иначе человек уверен, что переключил.
        this.agentMode = previous
        this.push({
          sender: 'bot',
          author: 'Cubus',
          text: `_Режим агента не переключился: ${e && e.message ? e.message : 'сервер не ответил'}._`,
        })
      }
    },

    onAgentKindChange(value) {
      if (value !== 'local') return
      this.bridgeConnected = isBridgeConnected()
      this.bridgeStatus = this.bridgeConnected ? 'connected' : 'idle'
      if (this.bridgeConnected) this.refreshBridgeReadiness()
    },

    // --- локальный Клод через мост (BRG-415) ---------------------------------
    onBridgeUrlChange(value) {
      this.bridgeUrl = setBridgeUrl(value)
    },

    /**
     * Подключение. Окно моста открывается ПРЯМО здесь, в обработчике нажатия: открытое позже,
     * из ответа сети, оно было бы для браузера непрошеным и попало бы под блокировщик.
     */
    /** PROP-754: нужно ли подтверждение моста. Не загрузилось — не спрашиваем здесь: сервер откажет при запуске, окно откроется тогда. */
    async loadBridgeConsent() {
      try {
        this.bridgeConsent = await bridgeConsentState()
      } catch (e) {
        this.bridgeConsent = { required: false, variant: 'B', orgName: '' }
      }
    },

    connectToBridge() {
      if (this.bridgeConsent.required) {
        this.bridgeConsentReason = 'connect'
        this.bridgeConsentChecked = false
        this.bridgeConsentOpen = true
        return
      }
      this.doConnectBridge(false)
    },

    /** «Подключить» в окне подтверждения — нажатие человека: мост открывается сразу, подтверждение пишется параллельно. */
    onBridgeConsentOk() {
      this.bridgeConsentOpen = false
      if (this.bridgeConsentReason === 'connect') {
        this.doConnectBridge(true)
        return
      }
      confirmBridgeConsent(this.bridgeConsentLang)
        .then(st => {
          this.bridgeConsent = { ...this.bridgeConsent, ...(st || {}), required: false }
          this.bridgeError = 'Подтверждение записано — отправьте вопрос ещё раз.'
        })
        .catch(e => { this.bridgeError = (e.response && e.response.data && e.response.data.error) || e.message })
    },

    async doConnectBridge(withConsent) {
      this.bridgeError = ''
      this.bridgeBusy = true
      this.bridgeStatus = 'connecting'
      try {
        const pending = connectBridge()
        if (withConsent) {
          // до первого запроса моста к Cubus (снимок домена ниже): без записи сервер ответит отказом
          const st = await confirmBridgeConsent(this.bridgeConsentLang)
          this.bridgeConsent = { ...this.bridgeConsent, ...(st || {}), required: false }
        }
        const c = await pending
        this.applyBridgeConnection(c)
        await this.refreshBridgeReadiness()
        // BRG-454: карта структуры домена — ЗДЕСЬ, при подключении, а не перед вопросом:
        // человек не должен ждать её, чтобы спросить. И её отсутствие не повод не работать.
        await this.syncBridgeDomain()
      } catch (e) {
        this.bridgeConnected = false
        this.bridgeStatus = 'offline'
        this.bridgeError = e.message
      } finally {
        this.bridgeBusy = false
      }
    },

    /**
     * BRG-454: принести мосту карту структуры домена.
     *
     * Ничего не бросает: карта — удобство агента, а не условие разговора. Исход уезжает
     * служебной строкой в «Отладку» — там же, где видны вехи хода работы: молчание здесь
     * читалось бы как «мост сломался», а это не так.
     */
    async syncBridgeDomain() {
      const domain = this.$store.getters['cubus-store/DOMAIN']
      const sid = domain && domain.sid ? domain.sid : ''
      let res = null
      try {
        res = await syncDomainSnapshot(sid)
      } catch (e) {
        res = { action: 'failed', reason: e.message }
      }
      const line = this.describeDomainSync(res)
      if (line) {
        this.push({
          sender: 'bot', author: 'Локальный Клод', at: new Date(), service: true, text: line,
        })
      }
    },
    describeDomainSync(res) {
      if (!res) return ''
      if (res.action === 'written') return `_Карта структуры домена обновлена (сборка ${res.build})._`
      if (res.action === 'confirmed') return `_Карта структуры домена уже свежая (сборка ${res.build})._`
      if (res.action === 'skipped') return `_Карта структуры домена не обновлялась: ${res.reason}._`
      return `_Карту структуры домена получить не удалось: ${res.reason}. На работу это не влияет._`
    },

    /** BUG-643: владелец сессии моста — адрес моста и папка гранта. */
    bridgeSessionOwnerKey() {
      return `${this.bridgeUrl}|${this.bridgeFolder}`
    },

    disconnectFromBridge() {
      disconnectBridge()
      this.bridgeConnected = false
      this.bridgeFolder = ''
      this.bridgeStatus = 'idle'
      this.bridgeError = ''
    },

    applyBridgeConnection(c) {
      this.bridgeConnected = !!c
      this.bridgeFolder = c ? c.folder : ''
      this.bridgeExpiresAt = c ? c.expiresAt : 0
      // BRG-437: инструменты разрешены ровно тогда, когда их подтвердил МОСТ. Раньше здесь
      // считалось, что достаточно получить токен у Cubus, — и надпись «Инструменты Cubus
      // подключены» стояла прямо над отказом моста «разрешено (ничего)».
      this.bridgeToolsAllowed = !!(c && c.apiOrigin)
      this.bridgeStatus = c ? 'connected' : 'idle'
      // RFILE-630: после переподключения (например, с «да» на третий вопрос) подсказка пересчитывается.
      this.bridgeResultHint = c ? this.resultFilesHintFor(this.bridgeResultFeature) : ''
    },

    /**
     * Готов ли Клод на той стороне. Отдельно от «мост подключён»: мост поднимается и тогда,
     * когда Claude Code не установлен, не залогинен или без плана — и тогда он жив, но работать
     * не может. Зелёный по одному лишь факту соединения был бы враньём.
     */
    async refreshBridgeReadiness() {
      try {
        const h = await bridgeHealth()
        this.bridgeStatus = h && h.claudeReady ? 'ready' : 'notready'
        this.bridgeError = h && h.claudeReady ? ''
          : 'Мост подключён, но Claude Code не готов: проверьте установку, вход («claude auth login») и активный план.'
      } catch (e) {
        this.bridgeStatus = 'offline'
        this.bridgeError = e.message
      }
    },

    /**
     * PROP-472.3: отчёт о прогоне в журнал Cubus.
     *
     * ЖУРНАЛ ВАЖЕН, НО НЕ ВАЖНЕЕ ОТВЕТА. Отправка идёт после того, как ответ уже показан
     * человеку, и её неудача не превращается в ошибку разговора: сообщать «не удалось
     * записать в журнал» тому, кто только что получил ответ, — значит пугать его тем, что
     * его не касается. Отказ уходит в консоль.
     */
    async reportRun(payload) {
      /**
       * BFILE-561 (ТЗ моста §2 п. 6, §8.2): отчёт несёт НОМЕР ПРОГОНА, момент окончания и
       * подтверждённые мостом вложения. По ним сервер пишет строку журнала И закрывает строку
       * следа: без номера закрывать нечего, без момента не посчитать исходы по файлам, а без
       * подтверждения след закрылся бы «файлы обработаны» там, где их не было.
       */
      const body = { ...payload }
      delete body.finishedAt
      delete body.attachmentsPlaced
      delete body.resultFiles
      /**
       * RFILE-631 (ТЗ «Мост: файлы результата» §2 п. 7, §6.2): места файлов результата — в отчёте. Сервер заводит
       * их и отдаёт пропуск ТОЛЬКО в ответе на этот отчёт; отказы проверок моста идут рядом.
       */
      if (payload.resultFiles) {
        body.files = payload.resultFiles.files.map(f => ({
          name: f.name, size: f.size, sha256: f.sha256, status: 'pending',
        }))
        body.files_failed = payload.resultFiles.failed
      }
      if (this.bridgeJobId) body.job_id = this.bridgeJobId
      if (payload.finishedAt) {
        // ISO-8601 С МИЛЛИСЕКУНДАМИ: округление до секунды ставило окончание раньше открывающей
        // строки следа, и процедура отвергала закрытие целиком (замер 81 мс, проба BFILE-548).
        body.finished_at = new Date(payload.finishedAt).toISOString()
      }
      if (Array.isArray(payload.attachmentsPlaced) && payload.attachmentsPlaced.length > 0) {
        body.attachments_placed = payload.attachmentsPlaced
      }
      try {
        const res = await reportLocalRun(body)
        /**
         * Сервер отвечает 200 и на отказ — полем `error` (BFILE-547): отчёт не принят, след не
         * закрыт. Разговору это не ошибка, но молчать о таком нельзя — иначе прогон выглядит
         * записанным, а в журнале его нет.
         */
        const data = (res && res.data) || {}
        if (data.logged === false) {
          // eslint-disable-next-line no-console
          console.warn('ai_activity: отчёт отклонён —', data.error || 'без причины')
        }
        return data
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('ai_activity: отчёт не отправлен —', (e && e.message) || e)
        return null
      }
    },

    async sendToBridge(text) {
      if (!isBridgeConnected()) {
        this.stopWaiting()
        this.applyBridgeConnection(null)
        this.push({
          sender: 'bot',
          author: 'Cubus',
          at: new Date(),
          service: true,
          text: '_Мост не подключён — нажмите «Подключить» и подтвердите доступ к папке._',
        })
        return
      }
      this.bridgeAsking = true
      this.bridgeRequestId = newRequestId()
      // PROP-456: источники — про ЭТОТ ответ. Не обнулив, мы бы приписали к новому ответу
      // данные, которых он не касался, — то есть соврали бы ровно там, где обещаем проверку.
      this.answerSources = []
      try {
        /**
         * Инструменты Cubus запрашиваются ПЕРЕД КАЖДЫМ вопросом: доступ выдаётся на минуты и
         * обращениями не продлевается (BRG-430). Отказ здесь не должен ронять разговор —
         * агент ответит без данных домена, и об этом честно говорится в панели.
         */
        let mcp = null
        /**
         * BFILE-559: это не только доступ к инструментам — это СТАРТ ПРОГОНА. Сервер выдаёт
         * номер, которым потом связываются запись памяти, отчёт и закрытие следа. Отказ здесь
         * по-прежнему не роняет разговор: агент ответит без данных домена, и панель скажет об этом.
         */
        this.bridgeJobId = ''
        this.bridgeRunFiles = []
        this.bridgeMemory = false
        this.bridgeResultFeature = null
        try {
          const started = await startLocalRun(this.attachments.map(a => a.id))
          mcp = started.mcp
          this.bridgeJobId = started.jobId
          this.bridgeRunFiles = started.files
          this.bridgeMemory = started.memory === true
          this.bridgeResultFeature = started.resultFiles
          this.bridgeResultHint = this.resultFilesHintFor(started.resultFiles)
          this.bridgeToolsOk = true
        } catch (e) {
          this.bridgeToolsOk = false
          this.bridgeError = `Инструменты Cubus не подключены: ${e.message}. Агент ответит, не видя данных домена.`
          // PROP-754: мост подключили до подтверждения — окно перед следующим запуском
          if (e.field === 'bridgeConsent') {
            this.bridgeConsent = { ...this.bridgeConsent, required: true }
            this.bridgeConsentReason = 'run'
            this.bridgeConsentChecked = false
            this.bridgeConsentOpen = true
            this.loadBridgeConsent().then(() => { this.bridgeConsent = { ...this.bridgeConsent, required: true } })
          }
        }
        /**
         * BFILE-560 (ТЗ моста §2, §4.2): байты — мосту ДО старта прогона.
         *
         * Передача не должна съедать срок токена агента, поэтому она идёт отдельным шагом и до
         * `/ask/stream`. Описания берём подтверждённые СЕРВЕРОМ (`bridgeRunFiles`), а байты — из
         * файла, выбранного человеком; его нет — страница скачает файл сама.
         *
         * Не передали хоть один — задание НЕ уходит. Молча отправить его без файла значило бы
         * получить уверенный ответ неизвестно о чём.
         */
        let runAttachments = null
        if (this.bridgeJobId && this.bridgeRunFiles.length > 0 && this.canAttach) {
          const withBytes = this.bridgeRunFiles.map(f => {
            const chosen = this.attachments.find(a => a.id === f.id)
            return { ...f, file: (chosen && chosen.file) || null }
          })
          try {
            runAttachments = await putAttachmentsToBridge(withBytes)
          } catch (e) {
            this.stopWaiting()
            this.bridgeAsking = false
            this.push({
              sender: 'bot', author: 'Cubus', at: new Date(), text: `⚠️ ${e.message} Задание не отправлено.`,
            })
            return
          }
        }
        /**
         * BUG-643: сессия Claude живёт в папке моста. Продолжать на другом мосту или в другой папке сессию,
         * начатую прежним, нельзя: мост запускает `claude --resume`, CLI не находит разговор и выходит с
         * ошибкой — на каждом сообщении до перезагрузки страницы. Сменились мост или папка — новый разговор.
         */
        if (this.bridgeSession && this.bridgeSessionOwner !== this.bridgeSessionOwnerKey()) {
          this.bridgeSession = ''
        }
        const res = await askBridgeStream({
          prompt: text,
          sessionId: this.bridgeSession,
          present: this.presentArmed,
          mcp,
          requestId: this.bridgeRequestId,
          onEvent: this.onBridgeEvent,
          jobId: this.bridgeJobId,
          attachments: runAttachments,
          memoryRun: this.bridgeMemory,
          resultFiles: this.bridgeResultFeature,
        })
        this.bridgeSession = res.sessionId || this.bridgeSession
        this.bridgeSessionOwner = this.bridgeSessionOwnerKey()
        // PROP-472.3: прогон в журнал ai_activity. ПОСЛЕ ответа и без ожидания — см. reportRun.
        // RFILE-631 (§7 пп. 4–5): отчёт не ждёт файлов, а передача ждёт ответа отчёта — пропуск в нём.
        const runJobId = this.bridgeJobId
        this.reportRun({
          prompt: text,
          result: res.text,
          sessionId: res.sessionId,
          ...res.usage,
          finishedAt: res.finishedAt,
          attachmentsPlaced: res.attachmentsPlaced,
          resultFiles: res.resultFiles,
        }).then(report => this.transferResultFiles(runJobId, res.resultFiles, report))
        // Дальше — ровно тот же разбор ответа, что и у демо-агента: страница из ```html-блока,
        // единственная внутренняя ссылка — на открытие. Мост блоков не выделяет (PROP-423 живёт
        // в раннере), поэтому работает запасной путь, который для того и оставлен.
        /**
         * RFILE-630 (ТЗ «Мост: файлы результата» §7 пп. 1–2): пузырь ответа моста несёт места «принимается»,
         * отказы моста и номер прогона — как ответ раннера. Кадр `files` из сокета найдёт его по `jobid`.
         */
        const described = res.resultFiles
        this.onServerMessage({
          type: 'message',
          kind: '',
          fromName: 'Локальный Клод',
          text: res.text,
          ts: Date.now(),
          jobid: this.bridgeJobId,
          files: described ? described.files.map(f => ({
            name: f.name, size: f.size, sha256: f.sha256, status: 'pending',
          })) : [],
          files_failed: described ? described.failed : [],
        })
        this.pushSources()
      } catch (e) {
        this.stopWaiting()
        // BRG-435: отмену человек сделал сам — это не отказ, и пугать значком её незачем.
        //
        // ⚠️ Но и СЛУЖЕБНОЙ её метить нельзя: вкладка «Чат» служебные строки прячет (PROP-397),
        // и человек, нажавший «Прервать», не видел бы вообще ничего. Найдено приёмкой в браузере:
        // строка в `messages` была, на экране её не было.
        const cancelled = e && e.code === 'CANCELLED'
        /**
         * PROP-472.3: неудачные прогоны пишутся ТОЖЕ — отменённые, упавшие по таймауту,
         * отбитые лимитом. Иначе в журнале останутся одни удачи, и по нему нельзя будет
         * понять, почему у человека «не работает».
         */
        /**
         * BFILE-561 (ТЗ моста §8.3): ПОЗДНИЙ ОТЧЁТ.
         *
         * Поток мог оборваться, пока прогон шёл, — это не «прогона не было». Мост помнит
         * последние итоги, и если по этой сессии он уже знает исход, отчитываемся ИМ: иначе в
         * журнале останется обрыв без исхода, а след — незакрытым.
         */
        let late = null
        if (!cancelled && this.bridgeJobId && this.bridgeSession) {
          try {
            const saved = await bridgeResult(this.bridgeSession)
            if (saved && saved.status === 'finished') late = saved
          } catch (err) { /* итога нет — отчитываемся отказом, как и раньше */ }
        }
        this.reportRun({
          prompt: text,
          result: (late && late.result) || '',
          sessionId: this.bridgeSession,
          error: late ? '' : ((cancelled ? 'Запрос прерван' : e && e.message) || 'Ошибка прогона'),
          ...((e && e.usage) || {}),
          // BFILE-561: момент окончания несёт и отказ — след закрывается и по неудаче. У позднего
          // отчёта момент берётся у моста же, поэтому с событием он не расходится.
          finishedAt: (late && late.finishedAt) || (e && e.finishedAt) || 0,
        })
        this.push({
          sender: 'bot',
          author: cancelled ? 'Cubus' : 'Локальный Клод',
          at: new Date(),
          text: cancelled ? 'Запрос прерван.' : `⚠️ ${e.message}`,
        })
      } finally {
        // BRG-434: снимаем запрет в `finally`, а не после `push` — иначе любой отказ (мост упал,
        // 409, таймаут) оставил бы поле запертым навсегда, и панель пришлось бы перезагружать.
        this.bridgeAsking = false
        this.bridgeCancelling = false
        this.bridgeRequestId = ''
      }
    },
    /**
     * BRG-441: скопировать строку установки.
     *
     * `navigator.clipboard` есть не везде (нужен защищённый контекст), поэтому запасной путь
     * через скрытое поле: команду человек должен унести в терминал, а не переписывать руками.
     */
    async copyInstallCommand() {
      const text = this.installCommand
      /**
       * ⚠️ Запасной путь нужен и тогда, когда `navigator.clipboard` ЕСТЬ, но ОТКАЗАЛ.
       *
       * Первая редакция включала его только при отсутствии API — и в приёмке кнопка не
       * скопировала ничего: буфер не изменился, надпись осталась «Копировать». `writeText`
       * отвергает обещание, когда у документа нет фокуса, и таких причин у него несколько.
       */
      const viaTextarea = () => {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.opacity = '0'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        el.remove()
        return ok
      }
      let copied = false
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          copied = true
        }
      } catch (e) {
        copied = false
      }
      if (!copied) {
        try { copied = viaTextarea() } catch (e) { copied = false }
      }
      // Не соврём: не получилось — так и напишем, команда всё равно на экране и её можно
      // выделить мышью.
      this.installCopyFailed = !copied
      this.installCopied = copied
      if (copied) setTimeout(() => { this.installCopied = false }, 2000)
    },

    /**
     * BRG-438: веха хода работы локального Клода — служебной строкой, как у раннера.
     *
     * Показываем ДЕЙСТВИЯ (какой инструмент и над чем), а не поток текста: дельты ответа идут
     * десятками на реплику и в ленте были бы шумом, тогда как «читает файл», «запускает
     * команду», «обращается к Cubus» отвечают на единственный вопрос, который возникает за
     * минуту ожидания, — чем он вообще занят.
     *
     * Строки служебные: в «Чате» их не видно, в «Отладке» видно (PROP-397) — ровно как у раннера.
     */
    onBridgeEvent(name, data) {
      const line = this.describeBridgeEvent(name, data)
      if (!line) return
      this.push({
        sender: 'bot', author: 'Локальный Клод', at: new Date(), service: true, text: line,
      })
    },
    /**
     * RFILE-631 (ТЗ «Мост: файлы результата» §2 пп. 7–8, §7 пп. 4–5, §12): ПЕРЕДАЧА ФАЙЛОВ РЕЗУЛЬТАТА.
     *
     * Ответ на отчёт — взгляд ПЛАТФОРМЫ на места: заведённые «принимается», отклонённые пределами или
     * «недоступны». Он сразу заменяет поля пузыря, как кадр. Затем открытые места передаются ПО ОДНОМУ:
     * мост → окно-посредник → страница → `result-file` с пропуском. Blob живёт ровно одну передачу — страница
     * не держит в памяти больше одного файла (§12), и ссылок на него после отправки не остаётся.
     *
     * Отказ передачи виден сразу строкой «не передан: …», не дожидаясь сторожа; место на платформе закроет
     * сторож, и его кадр `files` заменит поля пузыря целиком.
     */
    async transferResultFiles(jobId, described, report) {
      if (!jobId || !described || !report) return
      if (Array.isArray(report.files) || Array.isArray(report.files_failed)) {
        this.applyFilesFrame({ jobid: jobId, files: report.files || [], files_failed: report.files_failed || [] })
      }
      const { pass } = report
      if (!pass) return
      const open = (report.files || []).filter(f => f && f.status === 'pending' && !f.id)
      for (let i = 0; i < open.length; i += 1) {
        const slot = open[i]
        const file = described.files.find(f => f.name === slot.name && f.sha256 === slot.sha256)
        if (file) {
          try {
            // eslint-disable-next-line no-await-in-loop
            let blob = await fetchResultFileFromBridge(described.requestId, file.sha256)
            // eslint-disable-next-line no-await-in-loop
            await uploadResultFileToCubus(pass, jobId, file.name, file.sha256, blob)
            blob = null
          } catch (e) {
            this.markResultNotTransferred(jobId, file.name, (e && e.message) || 'ошибка передачи')
          }
        }
      }
    },
    /** RFILE-631: «не передан» — сразу в пузырь; кадр сторожа потом заменит поля целиком. */
    markResultNotTransferred(jobId, name, why) {
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        const m = this.messages[i]
        if (m.jobid === jobId) {
          this.$set(this.messages, i, {
            ...m,
            pendingFiles: (m.pendingFiles || []).filter(f => f.name !== name),
            filesFailed: [...(m.filesFailed || []).filter(f => f.name !== name), { name, reason: `не передан: ${why}` }],
          })
          return
        }
      }
    },
    /**
     * RFILE-630 (ТЗ «Мост: файлы результата» §19 п. 3): платформа файлы результата мосту даёт (фоновая приёмка),
     * а этот мост — нет. Причины разные, и совет должен быть свой: «обновите мост» тому, кто просто не дал
     * разрешения третьим вопросом, не поможет. Пусто — подсказывать нечего.
     */
    resultFilesHintFor(feature) {
      if (!feature || feature.enabled !== true || feature.intake !== 'background') return ''
      if (bridgeCanResultFiles()) return ''
      if (!bridgeBuild()) return 'Этот мост старый и файлы в Cubus не отправляет — обновите его: claude-bridge update.'
      // BUG-641: мост до выпуска о третьем вопросе не знает — переподключение ему не поможет, только обновление.
      if (!bridgeKnowsResultFiles()) {
        return `Этот мост файлы в Cubus отправлять не умеет — обновите его: claude-bridge update (сборка ${bridgeBuild()}).`
      }
      if (!bridgeResultFilesAllowed()) {
        return 'Отправка созданных агентом файлов в Cubus этому доступу не разрешена — нажмите «Отключить», '
          + 'подключитесь заново и разрешите её третьим вопросом в окне согласия.'
      }
      return `Этот мост файлы в Cubus отправлять не умеет — обновите его: claude-bridge update (сборка ${bridgeBuild()}).`
    },
    describeBridgeEvent(name, data) {
      if (name === 'accepted') {
        return data && data.queued
          ? '_Передано локальному Клоду — ждёт освобождения очереди._'
          : '_Передано локальному Клоду._'
      }
      if (name !== 'message' || !data) return ''
      // Запуск: сразу видно, доехали ли инструменты Cubus. Это первое, о чём спрашивают,
      // когда агент отвечает «не вижу данных домена».
      if (data.type === 'system' && data.subtype === 'init') {
        const servers = Array.isArray(data.mcp_servers)
          ? data.mcp_servers.map(x => (typeof x === 'string' ? x : x && x.name)).filter(Boolean)
          : []
        return `_Клод начал работу. Инструменты Cubus: ${servers.length ? servers.join(', ') : 'нет'}._`
      }
      if (data.type !== 'assistant') return ''
      const blocks = (data.message && data.message.content) || []
      const steps = blocks.filter(b => b && b.type === 'tool_use').map(b => this.describeTool(b))
      return steps.length ? steps.filter(Boolean).map(t => `_${t}_`).join('\n') : ''
    },
    /**
     * PROP-456: сводка «На чём основан ответ» — ОБЫЧНЫМ сообщением, а не служебным.
     *
     * ⚠️ Именно поэтому не `service: true`: вкладка «Чат» служебные строки прячет (PROP-397), а
     * проверить данные нужно как раз тому, кто задал вопрос, а не тому, кто читает отладку. В
     * «Отладке» строка тоже видна — там она стоит рядом с самими вызовами и не мешает.
     *
     * Пусто — молчим: агент, ответивший из общих знаний, ничего не читал, и обещать проверку
     * данных было бы неправдой.
     */
    pushSources() {
      const line = renderSourcesLine(this.answerSources)
      if (!line) return
      this.push({
        sender: 'bot', author: 'Cubus', at: new Date(), text: line,
      })
    },
    /**
     * PROP-456: источник данных из вызова — и в служебную строку, и в копилку для сводки.
     *
     * Копим ЗДЕСЬ, а не при разборе ответа: в ответе аргументов вызова уже нет, там только текст
     * модели, а ссылку надо строить из того, что реально ушло в платформу.
     */
    noteToolSource(block) {
      const domain = this.$store.getters['cubus-store/DOMAIN']
      const src = sourceOfToolCall(
        { name: block && block.name, input: block && block.input },
        domain && domain.sid ? domain.sid : '',
      )
      if (src) this.answerSources = addSource(this.answerSources, src)
      return src
    },
    describeTool(block) {
      const input = block.input || {}
      // Путь показываем именем файла: полный путь — сведения о машине, а в ленте от него
      // только шум. Тому, кто хочет видеть целиком, есть журнал моста.
      const file = String(input.file_path || input.path || '').split('/').pop()
      switch (block.name) {
        case 'Read': return file ? `Читает файл ${file}` : 'Читает файл'
        case 'Edit': return file ? `Правит файл ${file}` : 'Правит файл'
        case 'Write': return file ? `Пишет файл ${file}` : 'Пишет файл'
        case 'Bash': {
          const cmd = String(input.command || '').replace(/\s+/g, ' ').trim()
          return cmd ? `Запускает: ${cmd.length > 70 ? `${cmd.slice(0, 70)}…` : cmd}` : 'Запускает команду'
        }
        default: {
          // PROP-456: рядом с вызовом — ссылка на те же данные. Человек читает «обращается к
          // cubus: cubus_query» и тут же может открыть ровно ту выборку, а не восстанавливать её
          // по памяти в панели фильтра.
          const src = this.noteToolSource(block)
          const tail = src ? ` — [проверить: ${src.label}](${src.href})` : ''
          const mcp = /^mcp__([^_]+)__(.+)$/.exec(block.name || '')
          if (mcp) return `Обращается к ${mcp[1]}: ${mcp[2]}${tail}`
          return `Инструмент ${block.name || '?'}${tail}`
        }
      }
    },

    /**
     * BRG-435: прервать текущий запрос к локальному Клоду.
     *
     * Ответ на сам `/ask` придёт отказом `CANCELLED` — его и разбирает `catch` выше, поэтому
     * здесь ни строки в журнал, ни снятия ожидания: одно событие — одна запись.
     */
    async cancelAsk() {
      if (!this.bridgeRequestId || this.bridgeCancelling) return
      this.bridgeCancelling = true
      try {
        await cancelBridge(this.bridgeRequestId)
      } catch (e) {
        // Не смогли отменить — говорим об этом, но ожидание не трогаем: запрос-то ещё идёт.
        this.bridgeCancelling = false
        this.push({
          sender: 'bot',
          author: 'Cubus',
          at: new Date(),
          service: true,
          text: `_Прервать не удалось: ${e.message}_`,
        })
      }
    },
    // PROP-418: поле стало многострочным, и форму Enter больше не отправляет сам — отправляем
    // руками, оставляя Shift+Enter переносу строки. `isComposing` — набор через IME ещё идёт,
    // там Enter подтверждает подсказку ввода, а не заканчивает сообщение.
    onEnter(e) {
      if (e.isComposing) return
      e.preventDefault()
      if (!this.text.trim()) return
      this.onSubmit()
    },
    onSubmit() {
      const chosen = this.commandHints[this.commandIndex]
      if (chosen) {
        const typed = this.text.trim()
        const rest = typed.toLowerCase().startsWith(chosen.cmd)
          ? typed.slice(chosen.cmd.length).trim()
          : null
        const complete = rest !== null && (!chosen.args || rest.length > 0)
        if (!complete) { this.pickCommand(chosen); return }
      }
      this.send()
    },

    // --- голосовой ввод (PROP-393) -------------------------------------------
    // Та же реализация, что на странице чата (`CbsChatbot.toggleVoiceInput`): распознанный
    // текст ДОБАВЛЯЕТСЯ к набранному, а не затирает его; отправку человек делает сам.
    toggleVoiceInput() {
      if (this.isRecording) {
        this.recognition.stop()
        return
      }
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      this.recognition = new SpeechRecognition()
      this.recognition.lang = 'ru-RU'
      this.recognition.interimResults = false
      this.recognition.continuous = false
      this.recognition.onresult = event => {
        const { transcript } = event.results[0][0]
        this.text += (this.text ? ' ' : '') + transcript
        this.$nextTick(() => { this.focusInput() })
      }
      this.recognition.onend = () => {
        this.isRecording = false
        this.$nextTick(() => { this.focusInput() })
      }
      this.recognition.onerror = () => { this.isRecording = false }
      this.recognition.start()
      this.isRecording = true
    },
    focusInput() {
      const el = this.$refs.panelInput
      if (el && el.$el) el.$el.focus()
    },

    // --- показ ---------------------------------------------------------------
    render(text) { return renderChatMarkdown(text) },
    onLogClick(e) {
      // BUG-439: адрес сочиняет модель. Ведёт в никуда — говорим об этом строкой в ленте, а не
      // уводим человека на 404 и не проглатываем клик молча.
      handleInternalClick(e, this.$router, href => {
        this.push({
          sender: 'bot',
          author: 'Cubus',
          at: new Date(),
          text: `Ссылка ведёт в несуществующий раздел: \`${href}\`. Похоже, агент её выдумал —`
            + ' попросите его назвать объект по имени.',
        })
      })
    },
    // PROP-400: тот же переход, что и по клику, но инициированный автоматически.
    openInternalHref(href) {
      if (this.$router.currentRoute.fullPath === href) return
      this.$router.push(href).catch(() => { /* дубль текущего адреса или отказ гарда */ })
    },
    time(at) {
      const d = at instanceof Date ? at : new Date(at)
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('ru-RU')
    },
  },
}
</script>

<style lang="scss">
/* Не scoped: правило сужения страницы относится к чужой разметке (`.app-content` вендорного
   шаблона), а атрибут scoped до неё не доходит — та же ловушка, что съела правила служебной
   строки в PROP-367. */
/* Сужаем страницу ПОЛЕМ, а не отступом: у `.app-content` свой правый отступ (гуттер темы), и
   `padding-right` его подменял — содержимое прижималось к панели вплотную. Поле складывается
   с отступом, поэтому зазор темы остаётся на месте. */
body.cbs-agent-docked .app-content {
  margin-right: var(--cbs-agent-width, 380px);
  transition: margin-right 0.15s;
}
</style>

<style lang="scss" scoped>
.cbs-agent-toggle {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1051;                 // выше вендорной шапки, иначе кнопка уезжает под неё
  width: 34px;
  height: 46px;
  border: none;
  border-radius: 8px 0 0 8px;
  background: #7367f0;
  color: #fff;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: right 0.15s;
}

.cbs-agent-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  background: var(--white, #fff);
  border-left: 1px solid #ebe9f1;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.12);
}

// «Поверх» и «рядом» отличаются не панелью, а страницей под ней: тень нужна только тому,
// кто накрывает чужое содержимое.
.cbs-agent-panel--docked {
  box-shadow: none;
}

.cbs-agent-resizer {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  transition: background 0.15s;

  &:hover,
  &--active {
    background: rgba(115, 103, 240, 0.35);
  }
}

.cbs-agent-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid #ebe9f1;
}

.cbs-agent-toolbar {
  display: flex;
  align-items: center;
  /* PROP-419: третий селект длиннее двух первых вместе взятых — пускаем его на свою строку,
     иначе в узкой панели все три сожмутся до нечитаемых огрызков. */
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 12px;
  border-bottom: 1px solid #ebe9f1;
}

.cbs-agent-kind-select {
  flex: 1 1 100%;
  min-width: 0;
  font-size: 12px;
}

/* PROP-425: настройка моста — короткая форма над лентой, а не отдельный экран: подключение
   нужно ровно в тот момент, когда человек выбрал локального Клода. */
.cbs-agent-bridge {
  padding: 10px 12px;
  border-bottom: 1px solid #ebe9f1;
  font-size: 12px;
}

.cbs-agent-bridge-title {
  font-weight: 600;
  margin-bottom: 6px;
}

.cbs-agent-bridge-note {
  color: #6e6b7b;
  line-height: 1.35;
  margin-bottom: 6px;
}

.cbs-agent-bridge-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cbs-agent-bridge-status {
  color: #6e6b7b;
}

.cbs-agent-bridge-error {
  margin-top: 6px;
  color: #ea5455;
  line-height: 1.35;
}

.cbs-agent-install {
  margin-top: 8px;
  border-top: 1px dashed #ebe9f1;
  padding-top: 6px;
}

.cbs-agent-install-toggle {
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: #7367f0;
  cursor: pointer;
}

.cbs-agent-install-body {
  margin-top: 6px;
  color: #6e6b7b;
  line-height: 1.4;
}

.cbs-agent-install-step {
  margin-bottom: 6px;
}

.cbs-agent-install-os {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
}

.cbs-agent-install-os-btn {
  border: 1px solid #ebe9f1;
  background: none;
  border-radius: 4px;
  padding: 2px 10px;
  font: inherit;
  font-size: 12px;
  color: #6e6b7b;
  cursor: pointer;
}

.cbs-agent-install-os-btn.active {
  border-color: #7367f0;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.08);
}

.cbs-agent-install-cmd {
  display: flex;
  /* Многострочная команда Windows: кнопку держим у верхней строки, а не по центру блока. */
  align-items: flex-start;
  gap: 6px;
  margin-top: 4px;
}

/* Команду показываем целиком и даём выделить: переписывать её руками человек не должен.
   white-space: pre — сохраняем переносы многострочной команды Windows. */
.cbs-agent-install-cmd code {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  white-space: pre;
  padding: 4px 6px;
  background: #f8f8f8;
  border-radius: 4px;
  user-select: all;
}

.cbs-agent-install-copy {
  flex: 0 0 auto;
  font-size: 11px;
}

.cbs-agent-install-note {
  margin-top: 4px;
  font-size: 11px;
}

.cbs-agent-mode-select {
  /* PROP-402: два селекта делят ширину тулбара поровну и не переполняют узкую панель. */
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
}

.cbs-agent-title {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.cbs-agent-name {
  font-size: 13px;
  font-weight: 600;
}

.cbs-agent-modebadge {
  /* PROP-468: подпись режима в шапке — рядом со статусом, не вместо него. Приглушённая:
     это состояние, а не тревога; «Конструктор» выделен, потому что он снимает защиту. */
  margin-left: auto;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 11px;
  white-space: nowrap;
  background: rgba(130, 134, 139, 0.16);
  color: #82868b;
}

.cbs-agent-modebadge--builder {
  background: rgba(255, 159, 67, 0.18);
  color: #ff9f43;
}

.cbs-agent-status {
  font-size: 11px;
}

.cbs-agent-actions {
  display: flex;
  gap: 4px;
  margin-left: auto;
}

.cbs-agent-mode.btn {
  padding: 4px 6px;
  line-height: 1;
}

.cbs-agent-log {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cbs-agent-empty {
  font-size: 12px;
  color: #b9b9c3;
  line-height: 1.5;
}

.cbs-agent-row {
  display: flex;

  &--user {
    justify-content: flex-end;
  }
}

.cbs-agent-bubble {
  max-width: 92%;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  word-break: break-word;

  &--bot {
    background: #f8f8f8;
  }

  &--user {
    background: #7367f0;
    color: #fff;
  }
}

.cbs-agent-meta {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 10px;
  opacity: 0.7;
  margin-bottom: 2px;
}

// RFILE-590: файлы результата под ответом.
.cbs-agent-files {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.cbs-agent-file {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 5px 8px;
  border: 1px solid #e0e0e6;
  border-radius: 6px;
  background: #fff;
  color: #5e5873;
  font-size: 12px;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #7367f0;
    color: #7367f0;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
}

.cbs-agent-file-icon {
  flex: none;
}

// Длинное имя, которое пишет агент, не должно распирать панель: режем многоточием, полное имя
// остаётся в подсказке кнопки.
.cbs-agent-file-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cbs-agent-file-size {
  flex: none;
  font-size: 11px;
  opacity: 0.7;
}

.cbs-agent-file-error {
  font-size: 11px;
  color: #ea5455;
}

.cbs-agent-files-pending {
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.8;
}
.cbs-agent-files-failed {
  margin-top: 6px;
  font-size: 11px;
  color: #82868b;
}

.cbs-agent-service {
  font-size: 11px;
  color: #b9b9c3;
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  word-break: break-word;

  ::v-deep p {
    display: inline;
    margin: 0;
  }
}

.cbs-agent-service-time {
  margin-left: 4px;
  opacity: 0.65;
  white-space: nowrap;
}

.cbs-agent-typing {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #b9b9c3;
}

/* PROP-408: секундомер ожидания. Моноширинные цифры — чтобы строка не дёргалась каждую секунду. */
.cbs-agent-elapsed {
  margin-left: 4px;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}

.cbs-agent-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #b9b9c3;
  animation: cbs-agent-bounce 1.2s ease-in-out infinite;

  &:nth-child(3) { animation-delay: 0.2s; }
  &:nth-child(4) { animation-delay: 0.4s; }
}

@keyframes cbs-agent-bounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-3px); }
}

.cbs-agent-input {
  display: flex;
  /* PROP-418: поле растёт вверх, кнопки остаются у нижнего края — иначе они уезжали бы
     к середине выросшего поля. */
  align-items: flex-end;
  gap: 6px;
  padding: 10px 12px 12px 16px;
  border-top: 1px solid #ebe9f1;
  position: relative;
}

/* PROP-418: переносы строк внутри поля должны быть видны как переносы, а не как один
   длинный абзац. */
.cbs-agent-textarea {
  line-height: 1.35;
  overflow-y: auto;
}

/* FILE-516: прикреплённые файлы — строкой над полем ввода. */
.cbs-agent-attachments {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px 6px;
  background: var(--white, #fff);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.cbs-agent-attachment {
  font-size: 12px;
  padding: 1px 6px;
  border-radius: 10px;
  background: rgba(115, 103, 240, 0.12);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cbs-agent-attachment-remove {
  border: 0;
  background: transparent;
  padding: 0 0 0 4px;
  line-height: 1;
  cursor: pointer;
}

/* Список всплывает НАД полем: поле у нижнего края панели, под ним места нет. */
.cbs-agent-commands {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(100% - 6px);
  max-height: 240px;
  overflow-y: auto;
  background: var(--white, #fff);
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  z-index: 10;
}

.cbs-agent-command {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 6px 10px;
  cursor: pointer;

  &--active {
    background: rgba(115, 103, 240, 0.1);
  }
}

.cbs-agent-command-name {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #7367f0;
  white-space: nowrap;
}

.cbs-agent-command-hint {
  font-size: 11px;
  color: #6e6b7b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
